  <template>
  <div class="h-screen flex flex-col overflow-hidden fixed inset-0">
    <div class="flex-1 overflow-y-auto overflow-x-hidden px-5">
      <div class="space-y-4 pb-4 mt-4 max-w-full">
        <!-- Test Run Header -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 w-full max-w-full overflow-x-hidden">
          <!-- Header: Back Button, ID, Name, Status, Action Buttons -->
          <div class="flex items-center justify-between h-16 px-5 min-w-0">
            <!-- Left: Back Button, ID & Name -->
            <div class="flex items-center flex-1 min-w-0">
              <HLButton
                id="testrun-detail-back"
                @click="goBack"
                variant="ghost"
                size="sm"
                class="flex-shrink-0"
              >
                <template #icon>
                  <Icon name="arrowLeft" size="sm" />
                </template>
              </HLButton>
              
              <div class="h-6 w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0"></div>
              
              <div class="flex items-center space-x-3 min-w-0 ml-3">
                <HLTag
                  v-if="testRun?.testrun_id"
                  color="primary"
                  size="lg"
                  :bordered="false"
                  round
                  class="shrink-0"
                >
                  <template #default>{{ testRun.testrun_id }}</template>
                </HLTag>
                
                <h3 class="font-semibold text-gray-900 dark:text-white truncate">
                  {{ testRun?.name || 'Loading...' }}
                </h3>
                
                <HLTag
                  :color="getStatusTagColor(testRun?.status)"
                  size="md"
                  :bordered="false"
                  round
                  class="shrink-0"
                >
                  <template #default>{{ getStatusLabel(testRun?.status) }}</template>
                </HLTag>

                <div class="h-6 w-px bg-gray-300 dark:bg-gray-600 flex-shrink-0 ml-2"></div>

                <div class="flex items-center space-x-1 flex-shrink-0 ml-2">
                  <Icon name="user" size="sm" class="text-green-500 shrink-0" />
                  <HLText variant="body-sm" class="text-gray-700 dark:text-gray-300 font-medium whitespace-nowrap">
                    {{ createdByName || 'Unknown User' }}
                  </HLText>
                </div>
              </div>
            </div>
            
            <!-- Right: Action Buttons -->
            <div class="flex items-center space-x-2 flex-shrink-0 ml-4">
            <!-- Pause Button -->
            <HLButton
              v-if="canEdit && testRun?.status === 'IN_PROGRESS'"
              id="testrun-pause"
              @click="pauseTestRun"
              variant="outline"
              size="sm"
            >
              <template #iconLeft>
                <Icon name="pause" size="sm" class="text-orange-500" />
              </template>
              Pause
            </HLButton>
            
            <!-- Resume Button -->
            <HLButton
              v-if="canEdit && testRun?.status === 'PAUSED'"
              id="testrun-resume"
              @click="resumeTestRun"
              variant="outline"
              size="sm"
            >
              <template #iconLeft>
                <Icon name="play" size="sm" class="text-green-500" />
              </template>
              Resume
            </HLButton>
            
            <!-- Cancel Button -->
            <HLButton
              v-if="canEdit && testRun?.status !== 'CANCELLED' && testRun?.status !== 'COMPLETED'"
              id="testrun-cancel"
              @click="cancelTestRun"
              variant="outline"
              size="sm"
            >
              <template #iconLeft>
                <Icon name="ban" size="sm" class="text-red-500" />
              </template>
              Cancel
            </HLButton>
            
            <!-- Reopen Button -->
            <HLButton
              v-if="canEdit && testRun?.status === 'CANCELLED'"
              id="testrun-reopen"
              @click="reopenTestRun"
              variant="outline"
              size="sm"
            >
              <template #iconLeft>
                <Icon name="refresh" size="sm" class="text-blue-500" />
              </template>
              Reopen
            </HLButton>
            
            <!-- Edit Button -->
            <HLButton
              id="testrun-edit"
              @click="editTestRun"
              variant="outline"
              size="sm"
              :disabled="!canEdit"
            >
              <template #iconLeft>
                <Icon name="edit" size="sm" class="text-blue-500" />
              </template>
              Edit
            </HLButton>
            
            <!-- Clone Button -->
            <HLButton
              v-if="canEdit"
              id="testrun-clone"
              @click="cloneTestRun"
              variant="outline"
              size="sm"
              :loading="cloning"
            >
              <template #iconLeft>
                <Icon name="copy" size="sm" class="text-green-500" />
              </template>
              Clone
            </HLButton>
          </div>
          </div>
        </div>
        

        <!-- 3-Column Layout: Chart, Key Metrics & Details, Comments & Activity -->
        <div class="grid gap-3 w-full max-w-full overflow-x-hidden" :style="{
          gridTemplateColumns: windowWidth >= 1920 
            ? '0.75fr 1fr 0.75fr' 
            : windowWidth >= 1280 
            ? '0.75fr 1fr 0.75fr'
            : windowWidth >= 768
            ? 'repeat(2, 1fr)'
            : '1fr'
        }">
          <!-- Column 1: Test Status Distribution (Pie Chart) -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col border-r border-gray-100 dark:border-gray-700 min-w-0 overflow-hidden" style="height: 550px;">
            <!-- Graph Title -->
            <div class="border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-center space-x-2">
                <Icon name="chartPie" size="md" class="text-blue-600 dark:text-blue-400" />
                <HLText variant="h6" class="font-bold text-gray-900 dark:text-white" style="font-weight: 700 !important;">Test Status Distribution</HLText>
              </div>
          </div>
          
            <div v-if="loadingCases" class="flex items-center justify-center flex-1">
              <HLSkeleton height="300px" width="100%" />
            </div>
            <template v-else>
              <div class="flex-1 flex items-center justify-center mt-2">
                <DoughnutChart
                  :data="chartData"
                  :height="280"
                  tooltip-label="TC"
                  empty-message="No test execution data available"
                />
            </div>
              
              <!-- Progress Bar Section -->
              <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between mb-2">
                  <HLText variant="body-sm" class="font-medium text-gray-700 dark:text-gray-300">
                    Overall Progress
                  </HLText>
                  <HLText variant="body-sm" class="font-bold" :class="progressPercentage === 100 ? 'text-blue-600 dark:text-blue-400' : 'text-blue-600 dark:text-blue-400'">
                    {{ progressPercentage }}%
                  </HLText>
            </div>
        
                <!-- Progress Bar -->
                <div class="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    class="absolute top-0 left-0 h-full transition-all duration-500 ease-out rounded-full"
                    :class="progressPercentage === 100 ? 'bg-green-500' : 'bg-blue-500'"
                    :style="{ width: `${progressPercentage}%` }"
                  ></div>
            </div>
        
                <!-- Progress Stats -->
                <div class="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                    <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Completed</HLText>
                    <HLText variant="body-sm" class="font-bold text-gray-900 dark:text-white">
                      {{ stats.passed + stats.failed + stats.retest + stats.skipped }}
                    </HLText>
              </div>
                  <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                    <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Remaining</HLText>
                    <HLText variant="body-sm" class="font-bold text-gray-900 dark:text-white">
                      {{ stats.notStarted + stats.inProgress }}
                    </HLText>
            </div>
                  <div class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
                    <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Total</HLText>
                    <HLText variant="body-sm" class="font-bold text-gray-900 dark:text-white">
                      {{ stats.total }}
                    </HLText>
            </div>
              </div>
            </div>
            </template>
        </div>
        
          <!-- Column 2: Key Metrics -->
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col border-r border-gray-100 dark:border-gray-700 min-w-0 overflow-hidden" style="height: 550px;">
            <div class="grid gap-6 flex-1 overflow-y-auto overflow-x-hidden min-w-0" style="grid-template-columns: 0.8fr 1.2fr;">
              <!-- Environment (Left Column 1) -->
              <div class="flex items-start space-x-2">
                <Icon :name="FORM_FIELD_ICONS.environment.name" size="sm" :class="FORM_FIELD_ICONS.environment.color" class="mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Environment</HLText>
                  <HLTag color="primary" size="sm" :bordered="false" class="mt-1">
                    <template #default>{{ environmentDisplay }}</template>
                  </HLTag>
                  </div>
                </div>

              <!-- URL to Validate (Right Column 1) -->
              <div class="flex items-start space-x-2">
                <Icon :name="FORM_FIELD_ICONS.urlToValidate.name" size="sm" :class="FORM_FIELD_ICONS.urlToValidate.color" class="mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">URL to Validate</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white font-medium break-words">
                    {{ testRun?.urlToValidate || 'Not provided' }}
                  </HLText>
                  </div>
                </div>

              <!-- ETA (Left Column 2) -->
              <div class="flex items-start space-x-2">
                <Icon :name="FORM_FIELD_ICONS.eta.name" size="sm" :class="FORM_FIELD_ICONS.eta.color" class="mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">ETA</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white font-medium">
                    {{ testRun?.eta ? formatDateOnly(testRun.eta) : 'Not set' }}
                  </HLText>
                </div>
              </div>

              <!-- PR Links (Right Column 2) -->
              <div class="flex items-start space-x-2">
                <Icon :name="FORM_FIELD_ICONS.prLinks.name" size="sm" :class="FORM_FIELD_ICONS.prLinks.color" class="mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">PR Links</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white font-medium break-words">
                    {{ testRun?.prLinks || 'Not provided' }}
                  </HLText>
                  </div>
                </div>

              <!-- Created On (Left Column 3) -->
              <div class="flex items-start space-x-2">
                <Icon name="calendar" size="sm" class="text-purple-500 mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Created On</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white font-medium">
                    {{ (testRun?.created_at || testRun?.createdAt) ? formatDateOnly(testRun.created_at || testRun.createdAt) : 'N/A' }}
                  </HLText>
                  </div>
                  </div>

              <!-- Description (Right Column 3) -->
              <div class="flex items-start space-x-2">
                <Icon :name="FORM_FIELD_ICONS.description.name" size="sm" :class="FORM_FIELD_ICONS.description.color" class="mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Description</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white">
                    {{ testRun?.description || 'No description available' }}
                  </HLText>
                </div>
              </div>

              <!-- Est. Time (Left Column 4) -->
              <div class="flex items-start space-x-2">
                <Icon name="clock" size="sm" class="text-indigo-500 mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Est. Time</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white font-medium">
                    {{ totalEstimatedTime }}
                  </HLText>
                  </div>
                </div>

              <!-- Execution Notes (Right Column 4) -->
              <div class="flex items-start space-x-2">
                <Icon :name="FORM_FIELD_ICONS.executionNotes.name" size="sm" :class="FORM_FIELD_ICONS.executionNotes.color" class="mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Execution Notes</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white">
                    {{ testRun?.executionNotes || 'No execution notes available' }}
                  </HLText>
        </div>
      </div>

              <!-- Completion Date (Left Column 5) -->
              <div class="flex items-start space-x-2">
                <Icon name="checkCircle" size="sm" class="text-orange-500 mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Completion</HLText>
                  <HLText variant="body-sm" class="text-gray-900 dark:text-white font-medium">
                    {{ (testRun?.status === 'COMPLETED' && testRun?.completed_at) ? formatDateOnly(testRun.completed_at) : 'Not completed' }}
                  </HLText>
                </div>
              </div>
              
              <!-- Linked Features (Right Column 5) -->
              <div class="flex items-start space-x-2">
                <Icon :name="FORM_FIELD_ICONS.features.name" size="sm" :class="`mt-0.5 shrink-0`" :style="{ color: TAB_ICON_COLORS.FEATURES }" />
                <div class="flex-1 min-w-0">
                  <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Linked Features</HLText>
                  <div v-if="linkedFeatures.length > 0" class="space-y-1">
                    <div 
                      v-for="feature in linkedFeatures" 
                      :key="feature.id"
                      class="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                      @click="viewFeature(feature.id)"
                      :title="`Click to view ${feature.featureId} - ${feature.title}`"
                    >
                      <HLTag color="primary" size="xs" :bordered="false">
                        <template #default>{{ feature.featureId }}</template>
                      </HLTag>
                      <HLText variant="body-sm" class="text-gray-900 dark:text-white truncate">
                        {{ feature.title }}
                      </HLText>
                </div>
                </div>
                  <HLText v-else variant="body-sm" class="text-gray-400 dark:text-gray-500 italic">
                    No features linked
                  </HLText>
              </div>
            </div>
            </div>
          </div>
        
        <!-- Column 4: Comments & Activity -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 flex flex-col w-full max-w-full overflow-hidden" style="height: 550px;">
          <!-- Tabs -->
          <HLTabs v-model:value="activeRightTab" size="sm" class="mb-4 flex-shrink-0 w-full max-w-full">
            <HLTab name="comments" class="whitespace-nowrap">
              <Icon :name="FORM_FIELD_ICONS.comments.name" size="sm" :class="FORM_FIELD_ICONS.comments.color" class="mr-1" />
              Comments
            </HLTab>
            <HLTab name="activity" class="whitespace-nowrap">
              <Icon :name="FORM_FIELD_ICONS.activity.name" size="sm" :class="FORM_FIELD_ICONS.activity.color" class="mr-1" />
              Activity
            </HLTab>
            <HLTab name="bugs" class="whitespace-nowrap">
              <Icon name="bug" size="sm" :style="{ color: TAB_ICON_COLORS.BUGS }" class="mr-1" />
              Bugs
            </HLTab>
          </HLTabs>

          <!-- Tab Content -->
          <div class="flex-1 min-h-0 flex flex-col overflow-hidden w-full max-w-full">
            <!-- Comments Tab -->
            <div v-if="activeRightTab === 'comments'" class="flex-1 flex flex-col min-h-0 overflow-hidden w-full max-w-full">
              <div class="flex-1 overflow-y-auto space-y-3 mb-0 pr-2">
                <div v-if="loadingComments" class="space-y-2">
                  <HLSkeleton v-for="i in 3" :key="i" height="60px" />
      </div>
                <div v-else-if="comments.length === 0" class="text-center py-8">
                  <Icon name="message" size="lg" class="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <HLText variant="body-sm" class="text-gray-500 dark:text-gray-400">No comments yet</HLText>
          </div>
                <div v-else class="space-y-3">
                  <div 
                    v-for="comment in comments" 
                    :key="comment.id"
                    class="flex space-x-3"
                  >
                    <div class="shrink-0">
                      <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <Icon name="user" size="sm" class="text-blue-600 dark:text-blue-400" />
        </div>
              </div>
                    <div class="flex-1 min-w-0">
                      <div class="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                        <div class="flex items-center justify-between mb-1">
                          <HLText variant="body-sm" class="font-semibold text-gray-900 dark:text-white">
                            {{ comment.userName || 'Unknown User' }}
                          </HLText>
                          <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">
                            {{ formatActivityTime(comment.timestamp) }}
                          </HLText>
            </div>
                        <HLText variant="body-sm" class="text-gray-700 dark:text-gray-300">
                          {{ comment.text }}
                        </HLText>
              </div>
            </div>
                  </div>
            </div>
          </div>

              <!-- Add Comment Input -->
              <div class="border-t border-gray-200 dark:border-gray-700 pt-4 flex-shrink-0">
                <div class="relative">
                  <HLInput
                    v-model="newComment"
                    type="textarea"
                    placeholder="Add a comment..."
                    :rows="3"
                    class="w-full pr-12"
                  />
                  <HLButton
                    @click="addComment"
                    :disabled="!newComment.trim() || submittingComment"
                    :loading="submittingComment"
                    color="blue"
                    variant="primary"
                    size="sm"
                    class="absolute top-1/2 -translate-y-1/2 right-2 p-2"
                    title="Send comment"
                  >
                    <HLIcon size="20" class="rotate-90">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                    </HLIcon>
                  </HLButton>
              </div>
            </div>
          </div>

            <!-- Activity Tab -->
            <div v-if="activeRightTab === 'activity'" class="flex flex-col h-full min-h-0 w-full max-w-full overflow-hidden">
              <div v-if="activities.length > 0" class="space-y-1 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-300 dark:hover:scrollbar-thumb-gray-600 scrollbar-track-transparent pr-2 pl-4 min-h-0">
                <div
                  v-for="(activity, index) in activities"
                  :key="index"
                  class="relative pl-6 pb-1 ml-1 border-l-2 border-gray-200 dark:border-gray-700 last:border-transparent"
                >
                  <!-- Activity Icon -->
                  <div class="absolute -left-4 top-1">
                    <div class="w-7 h-7 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-800"
                      :class="getActivityIconClass(activity.type)"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path v-if="activity.type === 'created'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        <path v-else-if="activity.type === 'status_changed'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path v-else-if="activity.type === 'status_update'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path v-else-if="activity.type === 'test_cases_added'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        <path v-else-if="activity.type === 'test_case_added'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        <path v-else-if="activity.type === 'test_cases_removed'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        <path v-else-if="activity.type === 'test_case_removed'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        <path v-else-if="activity.type === 'test_run_updated'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        <path v-else-if="activity.type === 'assignee_updated'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        <path v-else-if="activity.type === 'bulk_update'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        <path v-else-if="activity.type === 'test_run_paused'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path v-else-if="activity.type === 'test_run_resumed'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path v-else-if="activity.type === 'test_run_cloned'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        <path v-else-if="activity.type === 'comment_added'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
        </div>
      </div>

                  <!-- Activity Content -->
                  <div>
                    <div class="flex items-center justify-between gap-1">
                      <p class="text-sm text-gray-900 dark:text-white flex-1">
                        <span class="font-semibold">{{ getActivityUserName(activity) }}</span> <span class="text-gray-600 dark:text-gray-400">{{ formatActivityDescription(activity) }}</span>
                      </p>
          </div>
                    <div class="flex items-center gap-2 mt-0">
                      <p 
                        class="text-xs text-gray-500 dark:text-gray-400 cursor-default"
                        :title="formatFullDateTime(activity.timestamp)"
                      >
                        {{ formatActivityTime(activity.timestamp) }}
                      </p>
        </div>
          </div>
        </div>
      </div>

              <div v-else class="text-gray-500 dark:text-gray-400 text-center py-4 flex-1 flex items-center justify-center">
                <div class="text-center">
                  <svg class="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
                  <p class="text-sm">No activities yet</p>
          </div>
        </div>
              </div>

            <!-- Bugs Tab -->
            <div v-if="activeRightTab === 'bugs'" class="flex flex-col h-full min-h-0 w-full overflow-hidden">
              <div v-if="loadingBugs" class="flex-1 flex items-center justify-center">
                <HLSpin size="lg" />
              </div>
              <div v-else-if="linkedBugs.length === 0" class="text-center py-8 flex-1 flex items-center justify-center">
                <div class="text-center">
                  <HLIcon size="48" class="text-gray-300 dark:text-gray-600 mx-auto mb-2">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </HLIcon>
                  <HLText variant="body-sm" class="text-gray-500 dark:text-gray-400">No bugs linked to this test run</HLText>
                </div>
              </div>
              <div v-else class="flex-1 overflow-y-auto space-y-2 pr-2 w-full min-w-0">
                <div
                  v-for="(bug, index) in linkedBugs"
                  :key="bug.id"
                  class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  @click="viewBugInNewTab(bug.id)"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-2">
                        <HLText variant="body-sm" class="text-gray-500 dark:text-gray-400 font-medium min-w-[30px]">
                          {{ index + 1 }}.
                        </HLText>
                        <HLTag color="primary" size="xs" :bordered="false">
                          <template #default>{{ bug.bugId }}</template>
                        </HLTag>
                        <HLText 
                          size="sm"
                          weight="bold"
                          html-tag="span"
                          class="text-gray-900 dark:text-white truncate"
                          :title="bug.title"
                        >
                          {{ truncateBugTitle(bug.title) }}
                        </HLText>
                      </div>
                      <HLSpace direction="horizontal" size="sm" class="flex-wrap">
                        <div class="flex items-center gap-1">
                          <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Priority:</HLText>
                          <HLTag 
                            :color="getBugPriorityColor(bug.priority)" 
                            size="xs" 
                            :bordered="false"
                          >
                            <template #default>{{ getBugPriorityLabel(bug.priority) }}</template>
                          </HLTag>
                        </div>
                        <div class="flex items-center gap-1">
                          <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Status:</HLText>
                          <HLTag 
                            :color="getBugStatusColor(bug.status)" 
                            size="xs" 
                            :bordered="false"
                          >
                            <template #default>{{ getBugStatusLabel(bug.status) }}</template>
                          </HLTag>
                        </div>
                        <div class="flex items-center gap-1">
                          <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Assignee:</HLText>
                          <HLText variant="body-xs" class="text-gray-900 dark:text-white font-medium">
                            {{ getBugAssigneeName(bug) }}
                          </HLText>
                        </div>
                      </HLSpace>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


        <!-- Test Cases Execution Section -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 w-full max-w-full overflow-x-hidden">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <Icon name="clipboardList" size="lg" class="text-indigo-500" />
              <div>
                <HLText variant="h5" class="font-bold text-gray-900 dark:text-white" style="font-weight: 700 !important;">Test Cases Execution</HLText>
                <HLText variant="body-xs" class="text-gray-500 dark:text-gray-400">Manage and execute test cases for this test run</HLText>
          </div>
            </div>
            <div class="flex items-center space-x-2">
          <HLButton
                id="testrun-toggle-filters"
                @click="showFilters = !showFilters"
                variant="outline"
            size="sm"
          >
            <template #iconLeft>
                  <Icon name="filter" size="sm" />
            </template>
                Filters
          </HLButton>
              <HLButton
                id="testrun-toggle-field-editor"
                @click="showFieldEditor = !showFieldEditor"
                variant="outline"
                size="sm"
              >
                <template #iconLeft>
                  <Icon name="settings" size="sm" />
                </template>
                Fields
              </HLButton>
            <HLButton
                v-if="testRunCases.length > 0"
                id="testrun-manage-cases"
                @click="editTestRun"
                variant="outline"
                size="sm"
                :disabled="!canEdit"
            >
              <template #iconLeft>
                  <Icon name="edit" size="sm" />
              </template>
                Manage
            </HLButton>
            <HLButton
                v-if="testRunCases.length > 0 && selectedTestCases.size > 0"
                id="testrun-remove-selected"
                @click="showRemoveCasesModal = true"
                variant="outline"
                size="sm"
            >
              <template #iconLeft>
                  <Icon name="trash" size="sm" class="text-red-500" />
              </template>
                Remove ({{ selectedTestCases.size }})
            </HLButton>
            </div>
                </div>
        </div>

        <!-- Filters Section -->
        <div v-if="showFilters" class="px-6 py-4 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center mb-4">
            <Icon name="filter" size="sm" class="text-indigo-600 dark:text-indigo-400 mr-2" />
            <HLText variant="h5" class="font-bold text-gray-900 dark:text-white" style="font-weight: 700 !important;">Filter Test Cases</HLText>
          </div>
          
          <!-- Search Bar -->
          <div class="mb-4">
            <HLInput
              v-model="filters.search"
              placeholder="Search test cases..."
              clearable
              class="w-full"
            >
              <template #prefix>
                <Icon name="search" size="sm" class="text-gray-400" />
              </template>
            </HLInput>
            </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
            <div>
              <HLText as="label" variant="label" class="block mb-1.5">Status</HLText>
              <HLSelect
                id="filter-status"
                v-model:value="filters.status"
                :options="testCaseExecutionStatusOptions"
                placeholder="All Statuses"
                clearable
                filterable
                multiple
                size="md"
              />
            </div>
            
            <div>
              <HLText as="label" variant="label" class="block mb-1.5">Type</HLText>
              <HLSelect
                id="filter-type"
                v-model:value="filters.type"
                :options="typeFilterOptions"
                placeholder="All Types"
                clearable
                filterable
                multiple
                size="md"
              />
            </div>
            
            <div>
              <HLText as="label" variant="label" class="block mb-1.5">Priority</HLText>
              <HLSelect
                id="filter-priority"
                v-model:value="filters.priority"
                :options="priorityFilterOptions"
                placeholder="All Priorities"
                clearable
                filterable
                multiple
                size="md"
              />
            </div>
            
            <div>
              <HLText as="label" variant="label" class="block mb-1.5">Case Type</HLText>
              <HLSelect
                id="filter-case-type"
                v-model:value="filters.caseType"
                :options="caseTypeFilterOptions"
                placeholder="All Case Types"
                clearable
                filterable
                multiple
                size="md"
              />
            </div>
            
            <div>
              <HLText as="label" variant="label" class="block mb-1.5">Automation</HLText>
              <HLSelect
                id="filter-automation-status"
                v-model:value="filters.automationStatus"
                :options="automationStatusFilterOptions"
                placeholder="All Automation"
                clearable
                filterable
                multiple
                size="md"
              />
            </div>
            
            <div>
              <HLText as="label" variant="label" class="block mb-1.5">Assignee</HLText>
              <HLSelect
                id="filter-assignee"
                v-model:value="filters.assignee"
                :options="assigneeFilterOptions"
                placeholder="All Assignees"
                clearable
                filterable
                multiple
                size="md"
              />
              </div>
            </div>
          
          <div class="flex justify-end">
            <HLButton id="testrun-clear-filters" @click="clearFilters" variant="secondary" color="gray" size="sm">
              Clear Filters
            </HLButton>
          </div>
        </div>

        <!-- Field Editor Section -->
        <div v-if="showFieldEditor" class="px-6 py-4 bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-800 dark:to-gray-800 border-b border-slate-200 dark:border-slate-700">
          <div class="flex items-center mb-3">
            <svg class="w-4 h-4 text-slate-600 dark:text-slate-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <HLText variant="h5" class="font-bold text-gray-900 dark:text-white" style="font-weight: 700 !important;">Customize Table Fields</HLText>
          </div>
          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div v-for="field in availableFields" :key="field.key" class="flex items-center space-x-2">
              <input
                :id="`field-${field.key}`"
                :checked="visibleFields.includes(field.key)"
                :disabled="field.mandatory"
                type="checkbox"
                @change="toggleField(field.key, field.mandatory, $event.target.checked)"
                class="w-3 h-3 text-slate-600 bg-white border-gray-300 rounded focus:ring-slate-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
              <label 
                :for="`field-${field.key}`" 
                :class="field.mandatory ? 'text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 cursor-pointer'"
                class="text-sm font-medium"
                @click="!field.mandatory && toggleField(field.key, field.mandatory, !visibleFields.includes(field.key))"
              >
                {{ field.label }}
                <span v-if="field.mandatory" class="text-xs text-red-500 ml-1">*</span>
              </label>
            </div>
          </div>
          <div class="flex justify-end mt-3">
            <HLButton id="testrun-reset-fields" @click="resetFields" variant="secondary" color="gray" size="sm" class="mr-2">
              Reset to Default
            </HLButton>
            <HLButton id="testrun-apply-fields" @click="applyFieldChanges" variant="primary" color="blue" size="sm">
              Apply Changes
            </HLButton>
          </div>
        </div>

        <!-- Selection Controls -->
        <div v-if="filteredTestCases.length > 0" class="px-8 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-b border-blue-200 dark:border-blue-700">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-6">
              <div class="flex items-center space-x-3">
                <HLCheckbox
                  :model-value="isAllSelected"
                  :indeterminate="isIndeterminate"
                  @update:checked="isAllSelected ? deselectAllTestCases() : selectAllTestCases()"
                  size="md"
                  id="select-all-test-cases"
                />
                <HLText variant="body-sm" class="font-semibold text-gray-900 dark:text-white">
                  {{ selectedTestCases.size }} of {{ filteredTestCases.length }} selected
                </HLText>
              </div>
              <div class="h-6 w-px bg-blue-300 dark:bg-blue-600"></div>
              <div class="flex items-center space-x-2">
                <Icon name="checkCircle" size="sm" class="text-gray-600 dark:text-gray-400" />
                <HLText variant="body-sm" class="text-gray-600 dark:text-gray-400">Bulk Actions Available</HLText>
              </div>
            </div>
            <div v-if="selectedTestCases.size > 0" class="flex items-center space-x-4">
                <div class="flex items-center space-x-2">
                <HLText variant="body-sm" class="font-medium text-gray-700 dark:text-gray-300">Update Status:</HLText>
                <HLSelect
                  id="bulk-update-status"
                  v-model:value="bulkUpdateStatus"
                  :options="testCaseExecutionStatusOptions"
                  placeholder="Select Status"
                  clearable
                  size="sm"
                  style="min-width: 150px;"
                />
              </div>
              
              <div class="flex items-center space-x-2">
                <HLText variant="body-sm" class="font-medium text-gray-700 dark:text-gray-300">Update Assignee:</HLText>
                <HLSelect
                  id="bulk-update-assignee"
                  v-model:value="bulkUpdateAssignee"
                  :options="assigneeFilterOptions"
                  placeholder="Select Assignee"
                  clearable
                  filterable
                  size="sm"
                  style="min-width: 150px;"
                />
              </div>
              
                <HLButton
                  id="testrun-bulk-update"
                  @click="bulkUpdateTestCases"
                :disabled="selectedTestCases.size === 0 || (!bulkUpdateStatus && !bulkUpdateAssignee)"
                  :loading="bulkUpdateLoading"
                  variant="primary"
                  color="blue"
                  size="sm"
                >
                  Update Selected
                </HLButton>
            </div>
          </div>
        </div>

        <div class="px-8 py-6" style="overflow-x: hidden;">
          <div v-if="loadingCases" class="py-12 space-y-4">
            <div class="space-y-3">
              <HLSkeleton height="60px" width="100%" />
              <HLSkeleton height="60px" width="100%" />
              <HLSkeleton height="60px" width="100%" />
              <HLSkeleton height="60px" width="100%" />
              <HLSkeleton height="60px" width="100%" />
            </div>
          </div>
          <div v-else-if="testRunCases.length === 0" class="py-16 text-center">
            <div class="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-3xl mb-6 shadow-lg">
              <Icon name="clipboardList" size="xl" class="text-gray-400" />
            </div>
            <HLText variant="h5" class="font-bold text-gray-900 dark:text-white mb-3">No Test Cases Found</HLText>
            <HLText variant="body-md" class="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              This test run doesn't have any test cases yet. Add test cases to start tracking execution progress.
            </HLText>
                  <HLButton
              v-if="canAddCases"
              id="testrun-add-cases-empty"
              @click="showAddCasesModal = true"
              variant="primary"
              color="blue"
              size="md"
                  >
              <template #iconLeft>
                <Icon name="plus" size="sm" />
              </template>
              Add Test Cases
                  </HLButton>
                </div>
          <div v-else class="space-y-6">
            <!-- Hierarchical Folder Structure -->
            <div v-if="testCasesByFolder.length > 0">
              <FolderNode
                v-for="folder in testCasesByFolder"
                :key="folder.id"
                :folder="folder"
                :table-headers="caseTableHeaders"
                :selected-test-cases="selectedTestCases"
                :team-members="teamMembers"
                :available-bugs="availableBugs"
                :can-edit="canEdit"
                :max-depth="999"
                @toggle-test-case-selection="toggleTestCaseSelection"
                @toggle-folder-selection="toggleFolderSelection"
                @update-test-case-status="updateTestCaseStatus"
                @update-test-case-assignee="updateTestCaseAssignee"
                @update-test-case-bugs="updateTestCaseBugs"
                @update-test-case-notes="updateTestCaseNotes"
                @create-bug-for-test-case="createBugForTestCase"
                @view-bug="handleBugClick"
                @open-test-case="openTestCase"
              />
          </div>
            
            <!-- No test cases message -->
            <div v-else class="p-8 text-center">
              <div class="inline-flex items-center justify-center w-12 h-12 bg-indigo-100 dark:bg-indigo-900 rounded-xl mb-4">
                <svg class="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">No test cases found</h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Try adjusting your filters or add test cases to this test run.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Test Cases Modal -->
      <Modal
        :isOpen="showAddCasesModal"
        @close="showAddCasesModal = false"
        title="Add Test Cases"
        size="lg"
      >
        <div class="space-y-4">
          <div>
            <HLText as="label" variant="label" class="block mb-1.5">Search Test Cases</HLText>
            <HLInput
              v-model="searchQuery"
              placeholder="Search test cases..."
              @input="searchTestCases"
              clearable
            >
              <template #prefix>
                <Icon name="search" size="sm" class="text-gray-400" />
              </template>
            </HLInput>
          </div>
          
          <div v-if="availableTestCases.length > 0" class="max-h-96 overflow-y-auto">
            <div class="space-y-2">
              <div
                v-for="testCase in availableTestCases"
                :key="testCase.id"
                class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg"
              >
                <div class="flex-1">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ testCase.name }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ testCase.description }}</p>
                </div>
                <HLButton
                  @click="addTestCaseToRun(testCase)"
                  size="sm"
                  :disabled="isTestCaseAdded(testCase.id)"
                >
                  {{ isTestCaseAdded(testCase.id) ? 'Added' : 'Add' }}
                </HLButton>
              </div>
            </div>
          </div>
          
          <div v-else-if="searching" class="text-center py-8 flex flex-col items-center justify-center">
            <HLSpin id="testrun-search-spinner" size="md" :show="searching">
              <template #description>
                <HLText variant="body-sm" color="muted">Searching test cases...</HLText>
              </template>
            </HLSpin>
          </div>
          
          <div v-else class="text-center py-8">
            <EmptyState
              title="No test cases found"
              description="Try adjusting your search criteria."
            />
          </div>
          
          <div class="flex justify-end space-x-3 pt-4">
            <HLButton
              type="button"
              variant="outline"
              @click="showAddCasesModal = false"
            >
              Close
            </HLButton>
          </div>
        </div>
      </Modal>


      <!-- Remove Test Cases Modal -->
      <Modal
        :isOpen="showRemoveCasesModal"
        @close="showRemoveCasesModal = false"
        :title="`Remove Test Cases (${selectedTestCases.size})`"
        size="md"
      >
        <div class="space-y-4">
          <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
            <div class="flex items-center">
              <svg class="w-5 h-5 text-red-600 dark:text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p class="text-sm text-red-800 dark:text-red-200">
                You are about to remove {{ selectedTestCases.size }} selected test case{{ selectedTestCases.size > 1 ? 's' : '' }} from this test run. This action cannot be undone.
              </p>
            </div>
          </div>

          <div class="max-h-64 overflow-y-auto space-y-2">
            <div
              v-for="testCase in selectedTestCasesForRemoval"
              :key="testCase.testCaseId"
              class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <div class="flex-1">
                <div class="flex items-center space-x-3">
                  <div class="bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded text-xs font-medium text-blue-600 dark:text-blue-400">
                    {{ testCase.testCaseId }}
                  </div>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ testCase.testCaseName }}</p>
                </div>
              </div>
              <HLButton
                @click="removeTestCase(testCase)"
                variant="outline"
                size="sm"
                class="text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Remove
              </HLButton>
            </div>
          </div>

          <!-- Modal Actions -->
          <div class="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-600">
            <div class="text-sm text-gray-500 dark:text-gray-400">
              {{ selectedTestCases.size }} test case{{ selectedTestCases.size > 1 ? 's' : '' }} selected
            </div>
            <div class="flex space-x-3">
              <HLButton
                @click="showRemoveCasesModal = false"
                variant="outline"
                size="sm"
              >
                Cancel
              </HLButton>
              <HLButton
                @click="removeAllSelectedTestCases"
                variant="outline"
                size="sm"
                class="text-red-600 dark:text-red-400 border-red-300 dark:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                Remove All Selected
              </HLButton>
            </div>
          </div>
        </div>
      </Modal>

      <!-- Edit Test Run Modal -->
      <Modal
        :isOpen="showEditModal"
        @close="showEditModal = false"
        title="Edit Test Run"
        size="md"
      >
        <div class="space-y-4">
          <div>
            <HLText as="label" variant="label" class="block mb-1.5">Test Run Name</HLText>
            <HLInput
              v-model="editForm.name"
              placeholder="Enter test run name"
              required
            />
          </div>
          
          <div>
            <HLText as="label" variant="label" class="block mb-1.5">Description</HLText>
            <HLInput
              v-model="editForm.description"
              type="textarea"
              placeholder="Enter test run description"
              :rows="3"
            />
          </div>
          
          <div>
            <HLText as="label" variant="label" class="block mb-1.5">Environment</HLText>
            <HLSelect
              v-model:value="editForm.environment"
              :options="environmentOptions"
              placeholder="Select Environment"
              clearable
            />
          </div>
        </div>
        
        <div class="flex justify-end space-x-3 mt-6">
          <HLButton
            @click="showEditModal = false"
            variant="outline"
            :disabled="editLoading"
          >
            Cancel
          </HLButton>
          <HLButton
            @click="updateTestRun"
            :loading="editLoading"
            color="primary"
          >
            Update Test Run
          </HLButton>
        </div>
      </Modal>
      </div>
      </div>
    </div>
  </template>

  <script setup>
  import { ref, computed, onActivated, onDeactivated, onMounted, onUnmounted, watch } from 'vue'
  import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
  import { useTestRunStore } from '@/stores/testRun'
  import { useTestCaseStore } from '@/stores/testCase'
  import { useAuthStore } from '@/stores/auth'
  import { useToast } from '@/composables/useToast'
  import { usePermissions } from '@/composables/usePermissions'
  import { useProjectAccess } from '@/composables/useProjectAccess'
  import { usePageTitle, formatDetailTitle } from '@/composables/usePageTitle'
import { TEST_CASE_EXECUTION_STATUS, TEST_CASE_EXECUTION_STATUS_VALUES, TEST_CASE_EXECUTION_STATUS_LEGACY, getTestCaseExecutionStatusColor, CASE_TYPES, AUTOMATION_STATUS } from '@/constants/testCaseConstants'
  import { TEST_RUN_STATUS_VALUES, TEST_RUN_STATUS, TEST_RUN_ENVIRONMENTS, getTestRunEnvironmentByValue, getTestRunStatusInfo } from '@/constants/testRunConstants'
  import { testCaseService, testRunService } from '@/database/services'
  import { realtimeHelpers } from '@/utils/realtime'
  import { HLButton, HLSpace, HLText, HLTag, HLTabs, HLTab, HLInput, HLSkeleton, HLDivider, HLSelect, HLCheckbox, HLIcon, HLSpin } from '@platform-ui/highrise'
  import { FORM_FIELD_ICONS } from '@/constants/ui'
  import { TAB_ICON_COLORS } from '@/constants/projects'
  import { BUG_PRIORITY_OPTIONS, BUG_STATUS_OPTIONS } from '@/constants/bugs'
  import { getHLTagColor } from '@/utils/badgeHelpers'
  import Icon from '@/components/ui/Icon.vue'
  import Table from '@/components/ui/Table.vue'
  import Modal from '@/components/ui/Modal.vue'
  import EmptyState from '@/components/ui/EmptyState.vue'
  import FolderNode from '@/components/FolderNode.vue'
  import DoughnutChart from '@/components/reports/DoughnutChart.vue'

  const route = useRoute()
  const router = useRouter()
  const testRunStore = useTestRunStore()
  const testCaseStore = useTestCaseStore()
  const authStore = useAuthStore()
  const { success: showToast, error: showError, info: showInfo } = useToast()
  const { can } = usePermissions()
  const { canCUDTestRuns } = useProjectAccess()
  const { setTitle } = usePageTitle()

  // API utility function
  const apiCall = async (endpoint) => {
    // Use the configured API base URL directly (works for both local and production)
    // Use nullish coalescing to handle empty strings correctly (enables Vite proxy)
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'https://dev.services.leadconnectorhq.com/hltestmanager'
    // If apiBaseUrl is empty (local dev), use relative URL with /hltestmanager prefix for Vite proxy
    const url = !apiBaseUrl || apiBaseUrl === ''
      ? `/hltestmanager/${endpoint}`
      : `${apiBaseUrl}/${endpoint}`
    return await fetch(url)
  }

  // Navigation function
  const goBack = async () => {
    // Proactively cleanup before navigating to avoid any async re-subscribe
    await cleanupRealtimeAndListeners()

    // Check if this test run is linked to a cross-team sanity request
    if (testRun.value?.cross_team_sanity_id) {
      router.push(`/cross-team-sanity/${testRun.value.cross_team_sanity_id}`)
      return
    }

    // Check if user came from assigned-to-me page
    const fromRoute = route.query.from
    if (fromRoute === 'assigned-to-me') {
      router.push('/assigned-to-me')
      return
    }
    
    // Check for projectId in different possible parameter names
    const projectId = route.params.projectId || testRun.value?.project_id
    
    // Always use explicit navigation instead of browser history
    if (projectId) {
      // Navigate to project test runs page
      router.push(`/projects/${projectId}/test-runs`)
    } else {
      // Fallback to general test runs page
      router.push('/test-runs')
    }
  }

  const loading = ref(false)
  const loadingCases = ref(false)
  const testRun = ref(null)
  const testRunCases = ref([])
  const hasProjectAccess = ref(false)
  
  // Real-time sync: Track last known updated_at timestamp and WebSocket subscription
  const lastKnownUpdatedAt = ref(null)
  const websocketUnsubscribe = ref(null) // Function to unsubscribe from WebSocket
  const pendingLocalChanges = ref(new Map()) // Track test cases with pending local changes (testCaseId -> timestamp)
  
  const showAddCasesModal = ref(false)
  const showRemoveCasesModal = ref(false)
  const showEditModal = ref(false)
  const availableTestCases = ref([])
  const searchQuery = ref('')
  const searching = ref(false)
  const starting = ref(false)
  const completing = ref(false)
  const cloning = ref(false)
  const createdByName = ref('')
  const teamMembers = ref([])
  const bugAssignees = ref(new Map()) // Map of userId -> user object for bug assignees
  const availableBugs = ref([])
  const linkedFeatures = ref([]) // Changed from linkedFeature to support multiple
  const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)

  // Edit form data
  const editForm = ref({
    name: '',
    description: '',
    environment: ''
  })
  const editLoading = ref(false)

  // Filter and selection data
  const selectedTestCases = ref(new Set())
  const showFilters = ref(false)
  const showFieldEditor = ref(false)
  const filters = ref({
    status: [],
    type: [],
    caseType: [],
    automationStatus: [],
    priority: [],
    assignee: [],
    search: ''
  })
  const bulkUpdateStatus = ref('')
  const bulkUpdateAssignee = ref('')
  const bulkUpdateLoading = ref(false)

  // Activities tracking
  const activities = ref([])
  const loadingActivities = ref(false)
  
  // Comments tracking
  const comments = ref([])
  const loadingComments = ref(false)
  const newComment = ref('')
  const submittingComment = ref(false)
  const activeRightTab = ref('comments')
  
  // Bugs tracking
  const linkedBugs = ref([])
  const loadingBugs = ref(false)

  // Selected test cases for removal
  const selectedTestCasesForRemoval = computed(() => {
    return testRunCases.value.filter(tc => selectedTestCases.value.has(tc.testCaseId))
  })

  // Field editor
  const getDefaultVisibleFields = () => ['select', 'testCaseId', 'testCase', 'status', 'type', 'caseType', 'automationStatus', 'priority', 'estimatedTime', 'assignee', 'bugs', 'executedAt', 'executedBy']
  
  // Load field selections from localStorage
  const loadFieldSelections = () => {
    try {
      const saved = localStorage.getItem('testRunDetailVisibleFields')
      if (saved) {
        const parsedFields = JSON.parse(saved)
        // Ensure mandatory fields are always included
        const mandatoryFields = ['select', 'testCaseId', 'testCase', 'status', 'bugs']
        const validFields = [...new Set([...mandatoryFields, ...parsedFields])]
        // If 'bugs' was missing from saved fields, add it now (migration for existing users)
        if (!validFields.includes('bugs')) {
          validFields.push('bugs')
        }
        return validFields
      }
    } catch (error) {
      // Error loading field selections, use defaults
    }
    return getDefaultVisibleFields()
  }
  
  // Save field selections to localStorage
  const saveFieldSelections = (fields) => {
    try {
      localStorage.setItem('testRunDetailVisibleFields', JSON.stringify(fields))
    } catch (error) {
      // Error saving field selections, continue silently
    }
  }
  
  const visibleFields = ref(loadFieldSelections())
  const availableFields = ref([
    { key: 'testCaseId', label: 'Test Case ID', mandatory: true },
    { key: 'testCase', label: 'Test Case', mandatory: true },
    { key: 'type', label: 'Type', mandatory: false },
    { key: 'caseType', label: 'Case Type', mandatory: false },
    { key: 'automationStatus', label: 'Automation', mandatory: false },
    { key: 'priority', label: 'Priority', mandatory: false },
    { key: 'estimatedTime', label: 'Estimate Time', mandatory: false },
    { key: 'status', label: 'Status', mandatory: true },
    { key: 'assignee', label: 'Assignee', mandatory: true },
    { key: 'bugs', label: 'Bugs', mandatory: true },
    { key: 'notes', label: 'Notes', mandatory: false },
    { key: 'executedAt', label: 'Executed At', mandatory: false },
    { key: 'executedBy', label: 'Executed By', mandatory: false }
  ])

  const caseTableHeaders = computed(() => {
    const otherHeaders = [
      { key: 'type', label: 'Type', width: '120px', align: 'center' },
      { key: 'caseType', label: 'Case Type', width: '120px', align: 'center' },
      { key: 'automationStatus', label: 'Automation', width: '120px', align: 'center' },
      { key: 'priority', label: 'Priority', width: '120px', align: 'center' },
      { key: 'estimatedTime', label: 'Estimate Time', width: '100px', align: 'center' },
      { key: 'assignee', label: 'Assignee', align: 'center' },
      { key: 'bugs', label: 'Bugs', width: '200px', align: 'center' },
      { key: 'notes', label: 'Notes', width: '200px', align: 'center' },
      { key: 'executedAt', label: 'Executed At', align: 'center' },
      { key: 'executedBy', label: 'Executed By', align: 'center' }
    ]
    
    const selectHeader = { key: 'select', label: '', width: '40px', align: 'center' }
    const testCaseIdHeader = { key: 'testCaseId', label: 'Test Case ID', width: '120px', align: 'center' }
    const testCaseHeader = { key: 'testCase', label: 'Test Case', align: 'center' }
    const statusHeader = { key: 'status', label: 'Status', align: 'center' }
    
    const visibleHeaders = []
    
    // Always add select column first
    visibleHeaders.push(selectHeader)
    
    // Always add test case ID column second
    visibleHeaders.push(testCaseIdHeader)
    
    // Always add test case column third
    visibleHeaders.push(testCaseHeader)
    
    // Add other visible headers in between
    const visibleOtherHeaders = otherHeaders.filter(header => visibleFields.value.includes(header.key))
    visibleHeaders.push(...visibleOtherHeaders)
    
    // Always add status column last
    visibleHeaders.push(statusHeader)
    
    return visibleHeaders
  })

  // Filtered test cases
  const filteredTestCases = computed(() => {
    let filtered = testRunCases.value

    if (filters.value.status && filters.value.status.length > 0) {
      filtered = filtered.filter(c => filters.value.status.includes(c.status))
    }
    if (filters.value.type && filters.value.type.length > 0) {
      filtered = filtered.filter(c => filters.value.type.includes(c.type))
    }
    if (filters.value.caseType && filters.value.caseType.length > 0) {
      filtered = filtered.filter(c => filters.value.caseType.includes(c.caseType))
    }
    if (filters.value.automationStatus && filters.value.automationStatus.length > 0) {
      filtered = filtered.filter(c => filters.value.automationStatus.includes(c.automationStatus))
    }
    if (filters.value.priority && filters.value.priority.length > 0) {
      filtered = filtered.filter(c => filters.value.priority.includes(c.priority))
    }
    if (filters.value.assignee && filters.value.assignee.length > 0) {
      const hasUnassigned = filters.value.assignee.includes('unassigned')
      const userIds = filters.value.assignee.filter(id => id !== 'unassigned')
      
      if (hasUnassigned && userIds.length > 0) {
        // Both unassigned and specific users
        filtered = filtered.filter(c => 
          (!c.assigneeId || c.assigneeId === '' || c.assigneeId === null || c.assigneeId === undefined) ||
          userIds.includes(c.assigneeId)
        )
      } else if (hasUnassigned) {
        // Only unassigned
        filtered = filtered.filter(c => !c.assigneeId || c.assigneeId === '' || c.assigneeId === null || c.assigneeId === undefined)
      } else if (userIds.length > 0) {
        // Only specific users
        filtered = filtered.filter(c => userIds.includes(c.assigneeId))
      }
    }
    if (filters.value.search) {
      const search = filters.value.search.toLowerCase()
      filtered = filtered.filter(c => 
        c.testCaseName.toLowerCase().includes(search) ||
        c.testCaseId.toLowerCase().includes(search)
      )
    }

    return filtered
  })

  // Build hierarchical folder structure based on folderId
  const testCasesByFolder = ref([])
  const cachedFolders = ref([]) // Cache folders to avoid reloading on every filter change
  
  // Load folders once when test run is loaded
  const loadFolders = async () => {
    if (!testRun.value?.project_id) return
    
    try {
      const { folderService } = await import('@/database/services')
      cachedFolders.value = await folderService.getByProject(testRun.value.project_id)
    } catch (error) {
      console.error('Error loading folders:', error)
      cachedFolders.value = []
    }
  }
  
  const buildFolderTree = async (testCases) => {
    try {
      // Only proceed if we have test cases
      if (!testCases || testCases.length === 0) {
        return []
      }
      
      // Use cached folders instead of reloading every time
      let allFolders = cachedFolders.value
      if (allFolders.length === 0 && testRun.value?.project_id) {
        // Only load if cache is empty
        try {
        await loadFolders()
        // CRITICAL FIX: Check if folders were actually loaded before retrying
        // This prevents infinite recursion if loadFolders() fails
        if (cachedFolders.value.length > 0) {
          return await buildFolderTree(testCases) // Retry after loading
        }
          // If loadFolders failed to populate cache, continue with empty folders
          // This will cause the main try block to create ungrouped display
          allFolders = []
        } catch (error) {
          // loadFolders failed - log warning and continue with empty folders
          // This ensures we fall through to display test cases ungrouped
          console.warn('Failed to load folders, test cases will be displayed ungrouped:', error)
          allFolders = []
        }
      }
      
      // Get unique folder IDs from test cases
      const testCaseFolderIds = [...new Set(testCases.map(tc => tc.folderId).filter(Boolean))]
      
      // Only include folders that have test cases in this test run
      const relevantFolders = allFolders.filter(folder => testCaseFolderIds.includes(folder.id))
      
      // Also include parent folders of relevant folders
      const includeParentFolders = (folderIds) => {
        const parentIds = new Set()
        folderIds.forEach(folderId => {
          const folder = allFolders.find(f => f.id === folderId)
          if (folder && folder.parentId) {
            parentIds.add(folder.parentId)
          }
        })
        return parentIds
      }
      
      // Recursively include all parent folders
      let allRelevantFolderIds = new Set(testCaseFolderIds)
      let parentIds = includeParentFolders(Array.from(allRelevantFolderIds))
      while (parentIds.size > 0) {
        parentIds.forEach(id => allRelevantFolderIds.add(id))
        parentIds = includeParentFolders(Array.from(parentIds))
      }
      
      // Filter to only include relevant folders
      const folders = allFolders.filter(folder => allRelevantFolderIds.has(folder.id))
      
      const tree = []
      const folderMap = new Map()
      
      // Create a map of folders by ID
      folders.forEach(folder => {
        folderMap.set(folder.id, {
          id: folder.id,
          name: folder.name,
          parentId: folder.parentId,
          testCases: [],
          children: [],
          level: 0
        })
      })
      
      // Build hierarchy and calculate levels
      const buildHierarchy = (parentId = null, level = 0) => {
        const children = folders.filter(f => f.parentId === parentId)
        return children.map(folder => {
          const folderNode = folderMap.get(folder.id)
          folderNode.level = level
          folderNode.children = buildHierarchy(folder.id, level + 1)
          return folderNode
        })
      }
      
      // Build the tree starting from root folders
      const rootFolders = buildHierarchy()
      tree.push(...rootFolders)
      
      // Add test cases to their respective folders
      testCases.forEach(testCase => {
        if (testCase.folderId && folderMap.has(testCase.folderId)) {
          folderMap.get(testCase.folderId).testCases.push(testCase)
        } else {
          // Add to root if no folderId
          if (!tree.find(f => f.id === 'root')) {
            tree.unshift({
              id: 'root',
              name: 'Root',
              parentId: null,
              testCases: [],
              children: [],
              level: 0
            })
          }
          const rootFolder = tree.find(f => f.id === 'root')
          rootFolder.testCases.push(testCase)
        }
      })
      
      // Filter out empty folders (folders with no test cases and no children with test cases)
      const filterEmptyFolders = (folderList) => {
        return folderList.filter(folder => {
          const hasTestCases = folder.testCases && folder.testCases.length > 0
          const hasChildrenWithTestCases = folder.children && folder.children.length > 0 && 
            filterEmptyFolders(folder.children).length > 0
          return hasTestCases || hasChildrenWithTestCases
        }).map(folder => ({
          ...folder,
          children: folder.children ? filterEmptyFolders(folder.children) : []
        }))
      }
      
      const result = filterEmptyFolders(tree)
      return result
    } catch (error) {
      // Error building folder tree, fallback to simple grouping
      const grouped = {}
      testCases.forEach(testCase => {
        const folderName = testCase.folderId ? `Folder ${testCase.folderId}` : 'Root'
        if (!grouped[folderName]) {
          grouped[folderName] = []
        }
        grouped[folderName].push(testCase)
      })
      
      return Object.entries(grouped).map(([name, testCases]) => ({
        id: name.toLowerCase().replace(/\s+/g, '-'),
        name,
        testCases,
        children: [],
        level: 0
      }))
    }
  }
  
  // Watch for changes in filtered test cases and rebuild tree
  watch(filteredTestCases, async (newTestCases) => {
    testCasesByFolder.value = await buildFolderTree(newTestCases)
  }, { immediate: true })

  // Selection helpers
  const isAllSelected = computed(() => {
    return filteredTestCases.value.length > 0 && 
           filteredTestCases.value.every(tc => selectedTestCases.value.has(tc.testCaseId))
  })

  const isIndeterminate = computed(() => {
    const selectedCount = filteredTestCases.value.filter(tc => selectedTestCases.value.has(tc.testCaseId)).length
    return selectedCount > 0 && selectedCount < filteredTestCases.value.length
  })

  const stats = computed(() => {
    const total = testRunCases.value.length
    const notStarted = testRunCases.value.filter(c => c.status === 'not_started').length
    const inProgress = testRunCases.value.filter(c => c.status === 'in_progress').length
    const passed = testRunCases.value.filter(c => c.status === 'passed').length
    const failed = testRunCases.value.filter(c => c.status === 'failed').length
    const retest = testRunCases.value.filter(c => c.status === 'retest').length
    const blocked = testRunCases.value.filter(c => c.status === 'blocked').length
    const skipped = testRunCases.value.filter(c => c.status === 'skipped').length
    return { total, notStarted, inProgress, passed, failed, retest, blocked, skipped }
  })

  // Progress percentage
  const progressPercentage = computed(() => {
    if (stats.value.total === 0) return 0
    // Completed = passed + failed + retest + skipped (anything that's not "not_started" or "in_progress")
    const completed = stats.value.passed + stats.value.failed + stats.value.retest + stats.value.skipped
    return Math.round((completed / stats.value.total) * 100)
  })

  // Chart data for DoughnutChart component
  const chartData = computed(() => {
    const data = []
    
    if (stats.value.notStarted > 0) {
      data.push({
        label: 'Not Started',
        value: stats.value.notStarted,
        color: 'rgba(55, 65, 81, 0.8)' // gray-700
      })
    }
    
    if (stats.value.inProgress > 0) {
      data.push({
        label: 'In Progress',
        value: stats.value.inProgress,
        color: 'rgba(37, 99, 235, 0.8)' // blue-600
      })
    }
    
    if (stats.value.passed > 0) {
      data.push({
        label: 'Passed',
        value: stats.value.passed,
        color: 'rgba(5, 150, 105, 0.8)' // green-600
      })
    }
    
    if (stats.value.failed > 0) {
      data.push({
        label: 'Failed',
        value: stats.value.failed,
        color: 'rgba(220, 38, 38, 0.8)' // red-600
      })
    }
    
    if (stats.value.retest > 0) {
      data.push({
        label: 'Retest',
        value: stats.value.retest,
        color: 'rgba(234, 88, 12, 0.8)' // orange-600
      })
    }
    
    if (stats.value.blocked > 0) {
      data.push({
        label: 'Blocked',
        value: stats.value.blocked,
        color: 'rgba(217, 119, 6, 0.8)' // yellow-600
      })
    }
    
    if (stats.value.skipped > 0) {
      data.push({
        label: 'Skipped',
        value: stats.value.skipped,
        color: 'rgba(124, 58, 237, 0.8)' // purple-600
      })
    }
    
    return data
  })

  // Helper function for pie chart calculations
  const getPieChartData = () => {
    const circumference = 2 * Math.PI * 35 // radius = 35
    const total = stats.value.total
    
    if (total === 0) {
      return {
        notStarted: '0 219.9',
        inProgress: '0 219.9',
        inProgressOffset: 0,
        passed: '0 219.9',
        passedOffset: 0,
        failed: '0 219.9',
        failedOffset: 0,
        retest: '0 219.9',
        retestOffset: 0,
        blocked: '0 219.9',
        blockedOffset: 0,
        skipped: '0 219.9',
        skippedOffset: 0
      }
    }

    const notStartedLength = (stats.value.notStarted / total) * circumference
    const inProgressLength = (stats.value.inProgress / total) * circumference
    const passedLength = (stats.value.passed / total) * circumference
    const failedLength = (stats.value.failed / total) * circumference
    const retestLength = (stats.value.retest / total) * circumference
    const blockedLength = (stats.value.blocked / total) * circumference
    const skippedLength = (stats.value.skipped / total) * circumference

    let currentOffset = 0

    return {
      notStarted: `${notStartedLength} ${circumference - notStartedLength}`,
      inProgress: `${inProgressLength} ${circumference - inProgressLength}`,
      inProgressOffset: -(currentOffset += notStartedLength),
      passed: `${passedLength} ${circumference - passedLength}`,
      passedOffset: -(currentOffset += inProgressLength),
      failed: `${failedLength} ${circumference - failedLength}`,
      failedOffset: -(currentOffset += passedLength),
      retest: `${retestLength} ${circumference - retestLength}`,
      retestOffset: -(currentOffset += failedLength),
      blocked: `${blockedLength} ${circumference - blockedLength}`,
      blockedOffset: -(currentOffset += retestLength),
      skipped: `${skippedLength} ${circumference - skippedLength}`,
      skippedOffset: -(currentOffset += blockedLength)
    }
  }


  const canEdit = computed(() => {
    if (!authStore.user || !testRun.value) return false
    
    // Check if user has test_runs:write permission
    if (!can.editTestRuns()) return false
    
    // Also check if user is part of the project (for non-super-admin users)
    return hasProjectAccess.value
  })

  const canStart = computed(() => {
    return canEdit.value
  })

  const canComplete = computed(() => {
    return canEdit.value
  })

  const canAddCases = computed(() => {
    return canEdit.value && testRun.value?.status !== 'COMPLETED'
  })

  const environmentDisplay = computed(() => {
    if (!testRun.value?.environment) return 'Not specified'
    
    const envConfig = getTestRunEnvironmentByValue(testRun.value.environment)
    return envConfig?.label || testRun.value.environment
  })

  // Description truncation
  const maxDescriptionLength = 200
  const descriptionLength = computed(() => {
    return testRun.value?.description?.length || 0
  })

  const isDescriptionTruncated = computed(() => {
    return descriptionLength.value > maxDescriptionLength
  })

  const truncatedDescription = computed(() => {
    const description = testRun.value?.description || 'No description available'
    if (description.length <= maxDescriptionLength) {
      return description
    }
    return description.substring(0, maxDescriptionLength) + '...'
  })

  // Filter options for HLSelect components
  const testCaseExecutionStatusOptions = computed(() => 
    TEST_CASE_EXECUTION_STATUS.map(status => ({
      label: status.label,
      value: status.value
    }))
  )

  const typeFilterOptions = computed(() => [
    { label: 'Functional', value: 'functional' },
    { label: 'API', value: 'api' },
    { label: 'UI', value: 'ui' },
    { label: 'Performance', value: 'performance' }
  ])

  const priorityFilterOptions = computed(() => [
    { label: 'Critical', value: 'critical' },
    { label: 'High', value: 'high' },
    { label: 'Medium', value: 'medium' },
    { label: 'Low', value: 'low' }
  ])

  const caseTypeFilterOptions = computed(() => 
    CASE_TYPES.map(type => ({
      label: type.label,
      value: type.value
    }))
  )

  const automationStatusFilterOptions = computed(() => 
    AUTOMATION_STATUS.map(status => ({
      label: status.label,
      value: status.value
    }))
  )

  const assigneeFilterOptions = computed(() => {
    const options = [{ label: 'Unassigned', value: 'unassigned' }]
    teamMembers.value.forEach(member => {
      options.push({
        label: member.name || member.email,
        value: member.id
      })
    })
    return options
  })

  // Environment options for edit form
  const environmentOptions = computed(() => 
    TEST_RUN_ENVIRONMENTS.map(env => ({
      value: env.value,
      label: env.label
    }))
  )

  // Calculate total estimated time for all test cases
  const totalEstimatedTime = computed(() => {
    if (!testRunCases.value || testRunCases.value.length === 0) {
      return '0m'
    }
    
    let totalMinutes = 0
    
    testRunCases.value.forEach(testCase => {
      const estimatedTime = testCase.estimatedTime || '0m'
      const timeStr = estimatedTime.toString().toLowerCase()
      
      // Parse different time formats
      if (timeStr.includes('h') && timeStr.includes('m')) {
        // Format: "2h 30m" or "1h 15m"
        const parts = timeStr.split(' ')
        parts.forEach(part => {
          if (part.includes('h')) {
            totalMinutes += parseInt(part.replace('h', '')) * 60
          } else if (part.includes('m')) {
            totalMinutes += parseInt(part.replace('m', ''))
          }
        })
      } else if (timeStr.includes('h')) {
        // Format: "2h" or "1.5h"
        const hours = parseFloat(timeStr.replace('h', ''))
        totalMinutes += Math.round(hours * 60)
      } else if (timeStr.includes('m')) {
        // Format: "30m" or "15m"
        totalMinutes += parseInt(timeStr.replace('m', ''))
      } else {
        // Try to parse as number (assume minutes)
        const minutes = parseInt(timeStr) || 0
        totalMinutes += minutes
      }
    })
    
    // Format the total time
    if (totalMinutes === 0) {
      return '0 Minutes'
    } else if (totalMinutes < 60) {
      return `${totalMinutes} Minutes`
    } else {
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      if (minutes === 0) {
        return `${hours} Hour${hours > 1 ? 's' : ''}`
      } else {
        return `${hours} Hour${hours > 1 ? 's' : ''} ${minutes} Minutes`
      }
    }
  })

  // Activity tracking functions
  const addActivity = async (type, description, details = {}) => {
    try {
      const testRunId = route.params.runId || route.params.id
      const activity = {
        type,
        description,
        userName: authStore.user?.name || authStore.user?.email || 'Unknown User',
        details
      }
      
      // Add to database (backend appends safely)
      const response = await testRunService.addActivity(testRunId, activity)
      
      // Update local state with the new activity from minimal response
      if (response?.activity) {
        // Backend returns minimal response with only the new activity
        activities.value.unshift(response.activity)
        if (activities.value.length > 50) {
          activities.value = activities.value.slice(0, 50)
        }
        // Update test run updated_at if provided
        if (response.updated_at) {
          testRun.value.updated_at = response.updated_at
        }
      } else {
        // Fallback: append locally if response format is unexpected
      const newActivity = {
        id: Date.now() + Math.random(),
        ...activity,
        timestamp: new Date()
      }
      activities.value.unshift(newActivity)
      if (activities.value.length > 50) {
        activities.value = activities.value.slice(0, 50)
        }
      }
    } catch (error) {
      // Error adding activity, continue silently
    }
  }

  const loadActivities = async () => {
    try {
      loadingActivities.value = true
      
      let response = []
      
      // OPTIMIZATION: Check if activities are already in testRun data (fastest path, no API call)
      if (testRun.value?.activities && Array.isArray(testRun.value.activities)) {
        response = testRun.value.activities
      } else {
      // Fallback to service call only if activities not in test run data
      const testRunId = route.params.runId || route.params.id
        response = await testRunService.getActivities(testRunId) || []
      }
      
      // OPTIMIZATION: Collect all unique user IDs that need to be resolved
      const userIdsToResolve = new Set()
      const activitiesToProcess = (response || []).map(activity => {
          // If activity already has user_name, use it
          if (activity.user_name || activity.userName) {
            activity.user_name = activity.user_name || activity.userName
            return activity
          }
          
        // Collect user IDs that need resolution
          const userId = activity.user_id || activity.created_by
          if (userId) {
          // Check if it's the current user first
          if (authStore.user && (authStore.user.id === userId || authStore.user.uid === userId)) {
            activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
            return activity
          }
          
          // Check if user is already in teamMembers
          const existingUser = teamMembers.value.find(u => u.id === userId || u.uid === userId)
          if (existingUser) {
            activity.user_name = existingUser.name || existingUser.displayName || existingUser.email || 'Unknown User'
            return activity
          }
          
          // Add to set of IDs to fetch
          userIdsToResolve.add(userId)
        }
        
        return activity
      })
      
      // OPTIMIZATION: Batch fetch all missing users in a single API call
      let userMap = new Map()
      if (userIdsToResolve.size > 0) {
                try {
          const { http } = await import('@/utils/httpClient')
          const userIdsArray = Array.from(userIdsToResolve)
          const users = await http.post('/users/batch-fetch', { userIds: userIdsArray })
          
          // Create a map for quick lookup
          users.forEach(user => {
            userMap.set(user.id, user)
            if (user.uid) userMap.set(user.uid, user)
          })
        } catch (batchError) {
          console.warn('Error batch fetching users for activities:', batchError)
                }
              }
              
      // Resolve user names using the batch-fetched users
      const activitiesWithUserNames = activitiesToProcess.map(activity => {
        // If already resolved, return as is
        if (activity.user_name) {
          return activity
        }
        
        const userId = activity.user_id || activity.created_by
        if (userId) {
          const user = userMap.get(userId) || teamMembers.value.find(u => u.id === userId || u.uid === userId)
              if (user) {
                activity.user_name = user.name || user.displayName || user.email || 'Unknown User'
                } else {
                  activity.user_name = 'Unknown User'
                }
              } else {
          // No user_id or created_by, use current user or Unknown
            if (authStore.user) {
              activity.user_name = authStore.user.name || authStore.user.email || 'Unknown User'
            } else {
              activity.user_name = 'Unknown User'
            }
          }
          
          return activity
        })
      
      // Filter out redundant generic "updated" activities when specific field_updated activities exist
      const filteredActivities = activitiesWithUserNames.filter((activity, index, arr) => {
        // Keep all non-updated activities (created, deleted, restored, etc.)
        if (activity.type !== 'updated' && activity.type !== 'test_run_updated') {
          return true
        }
        
        // For generic "updated" activities, check if there are specific field_updated activities
        // at the same time (within 1 second) from the same user
        const activityTime = activity.timestamp ? new Date(activity.timestamp).getTime() : 0
        const activityUserId = activity.user_id || activity.created_by
        
        // Check if there are any field_updated activities at the same time
        const hasSpecificFieldUpdates = arr.some(otherActivity => {
          if (otherActivity === activity) return false
          if (otherActivity.type !== 'field_updated') return false
          
          const otherTime = otherActivity.timestamp ? new Date(otherActivity.timestamp).getTime() : 0
          const otherUserId = otherActivity.user_id || otherActivity.created_by
          const timeDiff = Math.abs(activityTime - otherTime)
          
          // Same user and within 1 second
          return otherUserId === activityUserId && timeDiff < 1000
        })
        
        // Remove generic "updated" activity if specific field updates exist
        return !hasSpecificFieldUpdates
      })
      
      // Sort activities in descending order (newest first)
      activities.value = filteredActivities.sort((a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0)
        const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0)
        return dateB - dateA
      })
    } catch (error) {
      console.error('❌ Error loading activities:', error)
      activities.value = []
    } finally {
      loadingActivities.value = false
    }
  }

  const scrollToActivities = () => {
    // Activities are now in the right column, no need to scroll
    // Just switch to the activity tab
    activeRightTab.value = 'activity'
  }

  const getActivityIcon = (type) => {
    const icons = {
      'status_update': 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      'test_case_added': 'M12 6v6m0 0v6m0-6h6m-6 0H6',
      'test_case_removed': 'M6 18L18 6M6 6l12 12',
      'test_run_updated': 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
      'assignee_updated': 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
      'bulk_update': 'M4 6h16M4 10h16M4 14h16M4 18h16',
      'test_run_paused': 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z',
      'test_run_resumed': 'M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h8a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2z',
      'test_run_cloned': 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
    }
    return icons[type] || 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  }

  const getActivityIconBg = (type) => {
    const backgrounds = {
      'status_update': 'bg-blue-100 dark:bg-blue-900',
      'test_cases_added': 'bg-green-100 dark:bg-green-900',
      'test_case_added': 'bg-green-100 dark:bg-green-900',
      'test_cases_removed': 'bg-red-100 dark:bg-red-900',
      'test_case_removed': 'bg-red-100 dark:bg-red-900',
      'test_run_updated': 'bg-purple-100 dark:bg-purple-900',
      'assignee_updated': 'bg-yellow-100 dark:bg-yellow-900',
      'bulk_update': 'bg-indigo-100 dark:bg-indigo-900',
      'test_run_paused': 'bg-yellow-100 dark:bg-yellow-900',
      'test_run_resumed': 'bg-green-100 dark:bg-green-900',
      'test_run_cloned': 'bg-blue-100 dark:bg-blue-900'
    }
    return backgrounds[type] || 'bg-gray-100 dark:bg-gray-700'
  }

  const getActivityIconColor = (type) => {
    const colors = {
      'status_update': 'text-blue-600 dark:text-blue-400',
      'test_cases_added': 'text-blue-600 dark:text-blue-400',
      'test_case_added': 'text-blue-600 dark:text-blue-400',
      'test_cases_removed': 'text-red-600 dark:text-red-400',
      'test_case_removed': 'text-red-600 dark:text-red-400',
      'test_run_updated': 'text-purple-600 dark:text-purple-400',
      'assignee_updated': 'text-yellow-600 dark:text-yellow-400',
      'bulk_update': 'text-indigo-600 dark:text-indigo-400',
      'test_run_paused': 'text-yellow-600 dark:text-yellow-400',
      'test_run_resumed': 'text-blue-600 dark:text-blue-400',
      'test_run_cloned': 'text-blue-600 dark:text-blue-400'
    }
    return colors[type] || 'text-gray-600 dark:text-gray-400'
  }

  const getActivityTypeColor = (type) => {
    const colors = {
      'status_update': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'test_cases_added': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'test_case_added': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'test_cases_removed': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'test_case_removed': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'test_run_updated': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'assignee_updated': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'bulk_update': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'test_run_paused': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'test_run_resumed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'test_run_cloned': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    }
    return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }

  const formatActivityTime = (timestamp) => {
    // Convert timestamp to Date object if it's not already
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatActivityDate = (timestamp) => {
    // Convert timestamp to Date object if it's not already
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      'PASSED': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'FAILED': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'BLOCKED': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'SKIPPED': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
      'NOT_STARTED': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'IN_PROGRESS': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'RETEST': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    }
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
  }

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return 'Not available'
    
    try {
      // Handle different date formats
      let dateObj
      if (typeof date === 'string') {
        dateObj = new Date(date)
      } else if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp
        dateObj = date.toDate()
      } else if (date instanceof Date) {
        dateObj = date
      } else {
        dateObj = new Date(date)
      }
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  // Helper function to format dates (date only, no time)
  const formatDateOnly = (date) => {
    if (!date) return 'Not set'
    
    try {
      // Handle different date formats
      let dateObj
      if (typeof date === 'string') {
        dateObj = new Date(date)
      } else if (date.toDate && typeof date.toDate === 'function') {
        // Firestore Timestamp
        dateObj = date.toDate()
      } else if (date instanceof Date) {
        dateObj = date
      } else {
        dateObj = new Date(date)
      }
      
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  const getStatusLabel = (status) => {
    return getTestRunStatusInfo(status).label
  }

  const getStatusBadgeClass = (status) => {
    return getTestRunStatusInfo(status).badgeColor
  }

  const getStatusTagColor = (status) => {
    const statusMap = {
      'YET_TO_START': 'gray',
      'IN_PROGRESS': 'blue',
      'PAUSED': 'yellow',
      'COMPLETED': 'green',
      'CANCELLED': 'red'
    }
    return statusMap[status] || 'gray'
  }

  const getActivityIconName = (type) => {
    const iconMap = {
      'status_update': 'checkCircle',
      'test_cases_added': 'plus',
      'test_case_added': 'plus',
      'test_cases_removed': 'trash',
      'test_case_removed': 'trash',
      'test_run_updated': 'edit',
      'assignee_updated': 'user',
      'bulk_update': 'list',
      'test_run_paused': 'pause',
      'test_run_resumed': 'play',
      'test_run_cloned': 'copy'
    }
    return iconMap[type] || 'clock'
  }

  // Add comment function
  const addComment = async () => {
    if (!newComment.value.trim()) return
    if (submittingComment.value) return

    try {
      submittingComment.value = true
      const testRunId = route.params.runId || route.params.id
      const userId = authStore.user?.id || 'unknown'
      const userName = authStore.user?.name || authStore.user?.email || 'Unknown User'
      
      if (!testRunId) {
        showError('Test run ID not found')
        return
      }
      
      const comment = {
        text: newComment.value.trim(),
        userName: userName,
        userId: userId,
        timestamp: new Date()
      }
      
      // Add to database
      const result = await testRunService.addComment(testRunId, comment)
      
      // Clear input immediately
      newComment.value = ''
      
      // Reload comments from server to get the correct format
      await loadComments()
      
      // Show success message
      
    } catch (error) {
      console.error('Error adding comment:', error)
      showError(error.message || 'Error adding comment')
    } finally {
      submittingComment.value = false
    }
  }

  // Load comments
  const loadComments = async () => {
    try {
      loadingComments.value = true
      const testRunId = route.params.runId || route.params.id
      
      if (!testRunId) {
        comments.value = []
        return
      }
      
      // Always fetch fresh comments from the service
      let commentsData = []
      
      // First try to get from service
      try {
        commentsData = await testRunService.getComments(testRunId)
      } catch (serviceError) {
        // Fallback to testRun.value.comments if service fails
        if (testRun.value?.comments) {
          commentsData = testRun.value.comments
        }
      }
      
      // If still no data, try from testRun.value
      if (!commentsData || commentsData.length === 0) {
        if (testRun.value?.comments) {
          commentsData = testRun.value.comments
        }
      }
      
      // Transform comments to match UI format (user_name -> userName, comment -> text)
      const transformedComments = (Array.isArray(commentsData) ? commentsData : [])
        .map(comment => {
          let timestamp = comment.timestamp || comment.created_at || new Date()
          if (typeof timestamp === 'string') {
            timestamp = new Date(timestamp)
          }
          
          return {
            id: comment.id || `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            userName: comment.user_name || comment.userName || 'Unknown User',
            text: comment.comment || comment.text || '',
            timestamp: timestamp,
            user_id: comment.user_id || comment.userId,
            created_at: comment.created_at || timestamp
          }
        })
        .filter(comment => {
          return comment.text && comment.text.trim().length > 0
        })
        .sort((a, b) => {
          return new Date(a.timestamp) - new Date(b.timestamp) // Oldest first - reverse order like ClickUp
        })
      
      comments.value = transformedComments
    } catch (error) {
      console.error('Error loading comments:', error)
      comments.value = []
    } finally {
      loadingComments.value = false
    }
  }

  // Load linked bugs from test run cases (optimized - loads all project bugs once, then filters)
  const loadLinkedBugs = async () => {
    try {
      loadingBugs.value = true
      
      if (!testRun.value || !testRun.value.test_cases || testRun.value.test_cases.length === 0) {
        linkedBugs.value = []
        return
      }
      
      const projectId = testRun.value.project_id
      if (!projectId) {
        linkedBugs.value = []
        return
      }
      
      // Collect all unique bug IDs from test cases
      const bugIds = new Set()
      testRun.value.test_cases.forEach(tc => {
        if (tc.bugIds && Array.isArray(tc.bugIds)) {
          tc.bugIds.forEach(bugId => {
            if (bugId) bugIds.add(bugId)
          })
        }
      })
      
      if (bugIds.size === 0) {
        linkedBugs.value = []
        return
      }
      
      // OPTIMIZATION: Load all bugs for the project in one API call, then filter
      // This is much more efficient than individual getById calls
      const { bugService } = await import('@/database/services')
      const allBugs = await bugService.getByProject(projectId)
      
      // Filter to only include bugs that are linked to this test run
      linkedBugs.value = allBugs.filter(bug => 
        bug && 
        !bug.is_deleted && 
        bugIds.has(bug.id)
      )
      
      // Collect all unique assignee IDs from the linked bugs
      const assigneeIds = new Set()
      linkedBugs.value.forEach(bug => {
        if (bug.assigned_to) {
          assigneeIds.add(bug.assigned_to)
        }
      })
      
      // Batch fetch all bug assignees (even if they're not in current team)
      if (assigneeIds.size > 0) {
        try {
          // Filter out assignees we already have from teamMembers
          const existingUserIds = new Set(teamMembers.value.map(m => m.id).concat(teamMembers.value.map(m => m.uid).filter(Boolean)))
          const missingAssigneeIds = Array.from(assigneeIds).filter(id => !existingUserIds.has(id))
          
          if (missingAssigneeIds.length > 0) {
            const { http } = await import('@/utils/httpClient')
            const users = await http.post('/users/batch-fetch', { userIds: missingAssigneeIds })
            
            // Store in bugAssignees map
            users.forEach(user => {
              if (user && user.id) {
                bugAssignees.value.set(user.id, user)
                // Also store by uid if different
                if (user.uid && user.uid !== user.id) {
                  bugAssignees.value.set(user.uid, user)
                }
              }
            })
          }
          
          // Also add team members to bugAssignees map for quick lookup
          teamMembers.value.forEach(member => {
            if (member.id) {
              bugAssignees.value.set(member.id, member)
            }
            if (member.uid && member.uid !== member.id) {
              bugAssignees.value.set(member.uid, member)
            }
          })
        } catch (error) {
          console.warn('Failed to fetch bug assignees:', error)
        }
      }
    } catch (error) {
      console.error('Error loading linked bugs:', error)
      linkedBugs.value = []
    } finally {
      loadingBugs.value = false
    }
  }

  // Handle bug click - opens in new tab for both normal click and Command/Ctrl+Click
  const handleBugClick = (bugId, event) => {
    // Prevent default behavior if event is provided
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    
    const projectId = route.params.projectId || testRun.value?.project_id
    if (!projectId || !bugId) {
      showError('Project context or bug ID not found')
      return
    }
    
    // Include testRunId in query params so bug detail page can navigate back to test run
    const testRunId = route.params.id || testRun.value?.id
    const url = `/projects/${projectId}/bugs/${bugId}?testRunId=${testRunId}`
    
    // Check for modifier keys (Command on Mac, Ctrl on Windows/Linux)
    if (event && (event.metaKey || event.ctrlKey)) {
      // Command/Ctrl + Click: Open in new tab
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      // Normal click: Also open in new tab
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  // View bug in new tab (kept for backward compatibility)
  const viewBugInNewTab = (bugId) => {
    handleBugClick(bugId, null)
  }

  // Get bug priority label
  const getBugPriorityLabel = (priority) => {
    if (!priority) return 'N/A'
    const option = BUG_PRIORITY_OPTIONS.find(opt => opt.value === priority)
    return option ? option.label : priority || 'N/A'
  }

  // Get bug priority color
  const getBugPriorityColor = (priority) => {
    if (!priority) return 'gray'
    const normalizedPriority = String(priority).toLowerCase().trim()
    const priorityMap = {
      'critical': 'red',
      'high': 'orange',
      'medium': 'yellow',
      'low': 'blue'
    }
    const color = priorityMap[normalizedPriority] || 'gray'
    return getHLTagColor(color)
  }

  // Get bug status label
  const getBugStatusLabel = (status) => {
    if (!status) return 'N/A'
    const option = BUG_STATUS_OPTIONS.find(opt => opt.value === status)
    return option ? option.label : status || 'N/A'
  }

  // Get bug status color
  const getBugStatusColor = (status) => {
    if (!status) return 'gray'
    const option = BUG_STATUS_OPTIONS.find(opt => opt.value === status)
    const color = option?.color || 'gray'
    return getHLTagColor(color)
  }

  // Get bug assignee name
  const getBugAssigneeName = (bug) => {
    if (!bug?.assigned_to) return 'Unassigned'
    
    // First check bugAssignees map (includes both team members and fetched assignees)
    const user = bugAssignees.value.get(bug.assigned_to)
    if (user) {
      return user.name || user.email || 'Unknown'
    }
    
    // Fallback to teamMembers (for backwards compatibility)
    const teamMember = teamMembers.value.find(u => u.id === bug.assigned_to || u.uid === bug.assigned_to)
    if (teamMember) {
      return teamMember.name || teamMember.email || 'Unknown'
    }
    
    return 'Unknown'
  }

  // Truncate bug title with ellipsis if it exceeds limit
  const truncateBugTitle = (title, maxLength = 80) => {
    if (!title) return ''
    if (title.length <= maxLength) return title
    return title.substring(0, maxLength) + '...'
  }

  // Calculate test run status based on test case results
  const calculateTestRunStatus = (testCases) => {
    if (!testCases || testCases.length === 0) {
      return 'YET_TO_START'
    }

    const totalCases = testCases.length
    const completedCases = testCases.filter(tc => 
      tc.status === 'passed' || 
      tc.status === 'failed' || 
      tc.status === 'retest' || 
      tc.status === 'skipped'
    ).length

    const inProgressCases = testCases.filter(tc => tc.status === 'in_progress').length
    const notStartedCases = testCases.filter(tc => tc.status === 'not_started').length

    // If all cases are completed
    if (completedCases === totalCases) {
      return 'COMPLETED'
    }
    
    // If any cases are in progress or completed
    if (inProgressCases > 0 || completedCases > 0) {
      return 'IN_PROGRESS'
    }
    
    // If all cases are not started
    if (notStartedCases === totalCases) {
      return 'YET_TO_START'
    }

    // Default fallback
    return 'IN_PROGRESS'
  }

  const getExecutedByName = (userId) => {
    // First check if it's the current user
    if (authStore.user?.id === userId) {
      return authStore.user.name || authStore.user.email || 'Current User'
    }
    
    // Check team members
    const teamMember = teamMembers.value.find(member => member.id === userId)
    if (teamMember) {
      return teamMember.name || teamMember.email || 'Unknown User'
    }
    
    // Check created by user (if available)
    if (createdByName.value && testRun.value?.created_by === userId) {
      return createdByName.value
    }
    
    return 'Unknown User'
  }

  const getCaseStatusVariant = (status) => {
    const variants = {
      [TEST_CASE_EXECUTION_STATUS_LEGACY.NOT_EXECUTED]: 'secondary',
      [TEST_CASE_EXECUTION_STATUS_LEGACY.PASSED]: 'success',
      [TEST_CASE_EXECUTION_STATUS_LEGACY.FAILED]: 'danger',
      'retest': 'warning',
      'blocked': 'warning',
      'skipped': 'info'
    }
    return variants[status] || 'secondary'
  }

  const getTypeVariant = (type) => {
    const variants = {
      'functional': 'primary',
      'non-functional': 'secondary',
      'api': 'info',
      'ui': 'success',
      'performance': 'danger',
      'security': 'danger'
    }
    return variants[type] || 'secondary'
  }

  const getPriorityVariant = (priority) => {
    const variants = {
      'critical': 'danger',
      'high': 'warning',
      'medium': 'info',
      'low': 'secondary'
    }
    return variants[priority] || 'secondary'
  }

  const canExecute = (testRunCase) => {
    if (!authStore.user) return false
    return testRunCase.status === TEST_CASE_EXECUTION_STATUS_LEGACY.NOT_EXECUTED && 
          (testRunCase.assigneeId === authStore.user.id || canEdit.value)
  }

  const canRemove = (testRunCase) => {
    return canEdit.value
  }

  // HYBRID APPROACH: Refresh test run data from DB before updates
  // This ensures we have the latest data and prevents conflicts
  const refreshTestRunData = async () => {
    if (!testRun.value?.id) {
      return
    }
    
    try {
      const testRunId = route.params.runId || route.params.id
      const response = await apiCall(`test_runs/${testRunId}`)
      
      if (!response.ok) {
        return
      }
      
      const freshData = await response.json()
      if (!freshData) {
        return
      }
      
      // Merge fresh data intelligently (only update if remote is newer)
      const freshUpdatedAt = freshData.updated_at ? new Date(freshData.updated_at).getTime() : 0
      const currentUpdatedAt = lastKnownUpdatedAt.value || 0
      
      if (freshUpdatedAt > currentUpdatedAt) {
        // Remote is newer - merge changes
        await mergeRemoteChanges(freshData)
        lastKnownUpdatedAt.value = freshUpdatedAt
      }
    } catch (error) {
      // Silent error - don't interrupt user experience
      console.warn('Error refreshing test run data:', error)
    }
  }

  // Real-time sync: Poll for updates from other users
  // Real-time sync: Subscribe to WebSocket updates for this test run
  const startWebSocketSubscription = async () => {
    // Unsubscribe from previous subscription if exists
    if (websocketUnsubscribe.value) {
      websocketUnsubscribe.value()
      websocketUnsubscribe.value = null
    }
    
    const testRunId = route.params.runId || route.params.id
    if (!testRunId || !testRun.value?.id) {
      return
    }
    
    try {
      // Ensure WebSocket is connected before subscribing
      const realtimeManager = await import('@/utils/realtime').then(m => m.default)
      const status = realtimeManager.getStatus()
      if (!status.isConnected) {
        await realtimeManager.connect()
      }
      
      // Subscribe to test run updates via WebSocket
      const unsubscribe = await realtimeHelpers.subscribeToTestRun(testRunId, (update) => {
        // Skip if we have pending local changes (don't overwrite user's edits)
        if (pendingLocalChanges.value.size > 0) {
          return
        }
        
        const { action, data } = update
        
        // Handle different update types
        if (action === 'test_case_updated' && data) {
          handleWebSocketTestCaseUpdate(data)
        } else if (action === 'activity_added' && data) {
          handleWebSocketActivityUpdate(data)
        } else if (action === 'updated' && data) {
          // General test run update
          handleWebSocketTestRunUpdate(data)
        }
      })
      
      websocketUnsubscribe.value = unsubscribe
    } catch (error) {
      console.warn('Failed to subscribe to WebSocket updates:', error)
    }
  }
  
  const stopWebSocketSubscription = async () => {
    if (websocketUnsubscribe.value) {
      websocketUnsubscribe.value()
      websocketUnsubscribe.value = null
    }
    
    // Disconnect WebSocket when leaving test run page
    try {
      const realtimeManager = await import('@/utils/realtime').then(m => m.default)
      realtimeManager.disconnect()
    } catch (error) {
      console.warn('Failed to disconnect WebSocket:', error)
    }
  }
  
  // Handle WebSocket test case update
  const handleWebSocketTestCaseUpdate = (data) => {
    if (!data.testCaseId || !testRun.value) {
      return
    }
    
    // Skip if this test case has pending local changes
    if (pendingLocalChanges.value.has(data.testCaseId)) {
      return
    }
    
    const { updatedTestCase, progress, status } = data
    
    // Update test case in testRun.value.test_cases
    const testCaseIndex = testRun.value.test_cases.findIndex(
      (tc) => tc.testCaseId === updatedTestCase.testCaseId
    )
    
    if (testCaseIndex !== -1 && updatedTestCase) {
      // Merge remote changes
      testRun.value.test_cases[testCaseIndex] = {
        ...testRun.value.test_cases[testCaseIndex],
        ...updatedTestCase,
      }
      
      // Trigger reactivity
      testRun.value.test_cases = [...testRun.value.test_cases]
    }
    
    // Update testRunCases for UI
    const testRunCaseIndex = testRunCases.value.findIndex(
      (tc) => tc.testCaseId === updatedTestCase.testCaseId
    )
    
    if (testRunCaseIndex !== -1 && updatedTestCase) {
      // Update assignee_name if assignee changed
      if (updatedTestCase.assigned_to !== undefined) {
        if (updatedTestCase.assigned_to) {
          const assignee = teamMembers.value.find(m => m.id === updatedTestCase.assigned_to)
          testRunCases.value[testRunCaseIndex].assignee_name = assignee 
            ? (assignee.name || assignee.email || null) 
            : null
        } else {
          testRunCases.value[testRunCaseIndex].assignee_name = null
        }
      }
      
      // Update executed_by_name if executed_by changed
      if (updatedTestCase.executed_by !== undefined) {
        if (updatedTestCase.executed_by) {
          const executor = teamMembers.value.find(m => m.id === updatedTestCase.executed_by)
          testRunCases.value[testRunCaseIndex].executed_by_name = executor 
            ? (executor.name || executor.email || null) 
            : null
        } else {
          testRunCases.value[testRunCaseIndex].executed_by_name = null
        }
      }
      
      // Update other fields
      testRunCases.value[testRunCaseIndex] = {
        ...testRunCases.value[testRunCaseIndex],
        status: updatedTestCase.status || testRunCases.value[testRunCaseIndex].status,
        assigneeId: updatedTestCase.assigned_to || '',
        executed_by: updatedTestCase.executed_by || null,
        notes: updatedTestCase.notes || '',
        executed_at: updatedTestCase.executed_at || null,
      }
      
      // Trigger reactivity
      testRunCases.value = [...testRunCases.value]
    }
    
    // Update top-level fields
    if (progress !== undefined) testRun.value.progress = progress
    if (status !== undefined) testRun.value.status = status
    if (updatedTestCase.updated_at) {
      lastKnownUpdatedAt.value = new Date(updatedTestCase.updated_at).getTime()
    }
    
    // Show notification for significant changes - DISABLED
    // Notifications disabled to avoid spamming users with real-time updates
    // if (updatedTestCase.status) {
    //   showInfo(`Test case ${updatedTestCase.testCaseId} status updated to: ${updatedTestCase.status}`)
    // }
    // if (updatedTestCase.assigned_to !== undefined) {
    //   const assigneeName = updatedTestCase.assigned_to 
    //     ? (teamMembers.value.find(m => m.id === updatedTestCase.assigned_to)?.name || 'Unknown')
    //     : 'Unassigned'
    //   showInfo(`Test case ${updatedTestCase.testCaseId} assignee updated to: ${assigneeName}`)
    // }
  }
  
  // Handle WebSocket activity update
  const handleWebSocketActivityUpdate = (data) => {
    if (!data.activity) {
      return
    }
    
    // Add new activity to activities array
    activities.value = [data.activity, ...activities.value]
    
    // Reload activities to ensure consistency
    loadActivities()
  }
  
  // Handle WebSocket test run update
  const handleWebSocketTestRunUpdate = (data) => {
    if (!testRun.value) {
      return
    }
    
    // Update top-level test run fields
    if (data.status !== undefined && data.status !== testRun.value.status) {
      testRun.value.status = data.status
      // showInfo(`Test run status updated to: ${data.status}`) // Disabled to avoid spam
    }
    if (data.progress !== undefined && data.progress !== testRun.value.progress) {
      testRun.value.progress = data.progress
    }
    if (data.updated_at) {
      testRun.value.updated_at = data.updated_at
      lastKnownUpdatedAt.value = new Date(data.updated_at).getTime()
    }
  }
  
  // Intelligently merge remote changes without overwriting local edits
  const mergeRemoteChanges = async (remoteData) => {
    if (!testRun.value || !remoteData) {
      return
    }
    
    // Update top-level test run fields (status, progress, etc.)
    if (remoteData.status !== testRun.value.status) {
      testRun.value.status = remoteData.status
      // showInfo(`Test run status updated to: ${remoteData.status}`) // Disabled to avoid spam
    }
    if (remoteData.progress !== undefined && remoteData.progress !== testRun.value.progress) {
      testRun.value.progress = remoteData.progress
    }
    if (remoteData.updated_at) {
      testRun.value.updated_at = remoteData.updated_at
    }
    
    // Merge test case changes intelligently
    if (remoteData.test_cases && Array.isArray(remoteData.test_cases)) {
      const remoteTestCases = remoteData.test_cases
      const localTestCases = testRun.value.test_cases || []
      
      // Create a map of remote test cases by testCaseId
      const remoteTestCaseMap = new Map()
      remoteTestCases.forEach(tc => {
        if (tc.testCaseId) {
          remoteTestCaseMap.set(tc.testCaseId, tc)
        }
      })
      
      // Update local test cases that don't have pending local changes
      localTestCases.forEach((localTc, index) => {
        if (!localTc.testCaseId) return
        
        // Skip if this test case has pending local changes
        if (pendingLocalChanges.value.has(localTc.testCaseId)) {
          return
        }
        
        const remoteTc = remoteTestCaseMap.get(localTc.testCaseId)
        if (!remoteTc) return
        
        // Compare timestamps to see if remote is newer
        const localUpdatedAt = localTc.updated_at ? new Date(localTc.updated_at).getTime() : 0
        const remoteUpdatedAt = remoteTc.updated_at ? new Date(remoteTc.updated_at).getTime() : 0
        
        if (remoteUpdatedAt > localUpdatedAt) {
          // Remote is newer - merge changes
          const hasChanges = 
            remoteTc.status !== localTc.status ||
            remoteTc.assigned_to !== localTc.assigned_to ||
            remoteTc.executed_by !== localTc.executed_by ||
            remoteTc.notes !== localTc.notes
          
          if (hasChanges) {
            // Update local test case with remote changes
            testRun.value.test_cases[index] = {
              ...localTc,
              ...remoteTc
            }
            
            // Also update testRunCases for UI consistency
            const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === localTc.testCaseId)
            if (testRunCaseIndex !== -1) {
              // Update assignee_name if assignee changed
              if (remoteTc.assigned_to !== localTc.assigned_to) {
                if (remoteTc.assigned_to) {
                  const assignee = teamMembers.value.find(m => m.id === remoteTc.assigned_to)
                  testRunCases.value[testRunCaseIndex].assignee_name = assignee ? (assignee.name || assignee.email || null) : null
                } else {
                  testRunCases.value[testRunCaseIndex].assignee_name = null
                }
              }
              
              // Update executed_by_name if executed_by changed
              if (remoteTc.executed_by !== localTc.executed_by) {
                if (remoteTc.executed_by) {
                  const executor = teamMembers.value.find(m => m.id === remoteTc.executed_by)
                  testRunCases.value[testRunCaseIndex].executed_by_name = executor ? (executor.name || executor.email || null) : null
                } else {
                  testRunCases.value[testRunCaseIndex].executed_by_name = null
                }
              }
              
              // Update other fields
              testRunCases.value[testRunCaseIndex] = {
                ...testRunCases.value[testRunCaseIndex],
                status: remoteTc.status || testRunCases.value[testRunCaseIndex].status,
                assigneeId: remoteTc.assigned_to || '',
                executed_by: remoteTc.executed_by || null,
                notes: remoteTc.notes || '',
                executed_at: remoteTc.executed_at || null
              }
            }
            
            // Show notification for significant changes
            if (remoteTc.status !== localTc.status) {
              showInfo(`Test case ${localTc.testCaseId} status updated to: ${remoteTc.status}`)
            }
            if (remoteTc.assigned_to !== localTc.assigned_to) {
              const assigneeName = remoteTc.assigned_to 
                ? (teamMembers.value.find(m => m.id === remoteTc.assigned_to)?.name || 'Unknown')
                : 'Unassigned'
              showInfo(`Test case ${localTc.testCaseId} assignee updated to: ${assigneeName}`)
            }
          }
        }
      })
      
      // Trigger reactivity
      testRun.value.test_cases = [...testRun.value.test_cases]
      testRunCases.value = [...testRunCases.value]
    }
    
    // Reload activities to show new activities from other users
    await loadActivities()
  }

  const loadTestRun = async () => {
    const totalStartTime = performance.now()
    let apiCallCount = 0 // Track optimized calls only
    let allApiCalls = [] // Track ALL API calls for detailed breakdown
    
    // Intercept fetch calls to track all API requests
    const originalFetch = window.fetch
    let fetchInterceptorActive = false
    
    const interceptFetch = () => {
      if (fetchInterceptorActive) return
      fetchInterceptorActive = true
      
      window.fetch = async (...args) => {
        const url = args[0]
        const isApiCall = typeof url === 'string' && (
          url.includes('/hltestmanager/') || 
          url.includes('dev.services.leadconnectorhq.com') ||
          url.includes('localhost:6050')
        )
        
        if (isApiCall) {
          const callStartTime = performance.now()
          const endpoint = url.split('/hltestmanager/')[1] || url.split('/').pop() || 'unknown'
          allApiCalls.push({ endpoint, startTime: callStartTime, status: 'pending' })
          
          try {
            const response = await originalFetch(...args)
            const callEndTime = performance.now()
            const lastCall = allApiCalls[allApiCalls.length - 1]
            if (lastCall && lastCall.endpoint === endpoint) {
              lastCall.status = response.status
              lastCall.duration = (callEndTime - callStartTime).toFixed(2)
            }
            return response
          } catch (error) {
            const callEndTime = performance.now()
            const lastCall = allApiCalls[allApiCalls.length - 1]
            if (lastCall && lastCall.endpoint === endpoint) {
              lastCall.status = 'error'
              lastCall.duration = (callEndTime - callStartTime).toFixed(2)
            }
            throw error
          }
        }
        
        return originalFetch(...args)
      }
    }
    
    const restoreFetch = () => {
      if (fetchInterceptorActive) {
        window.fetch = originalFetch
        fetchInterceptorActive = false
      }
    }
    
    try {
      interceptFetch() // Start tracking all API calls
      
      loading.value = true
      const testRunId = route.params.runId || route.params.id
      
      // Check if route param exists
      if (!testRunId) {
        showError('Test run ID not found in URL')
        return
      }
      
      // OPTIMIZATION: Load test run basic info FIRST (fast, lightweight) - allows UI to show immediately
      const response = await apiCall(`test_runs/${testRunId}`)
      apiCallCount++
      
      if (!response.ok) {
        showError('Test run not found')
        return
      }
      
      const data = await response.json()
      
      if (!data) {
        showError('Test run not found')
        return
      }
      
      testRun.value = data
      
      // Update document title with test run ID
      const idToUse = data?.testrun_id || testRunId
      if (idToUse) {
        setTitle(formatDetailTitle('Test Run', idToUse, data?.name || data?.title))
      }
      
      // Track last known updated_at for real-time sync
      lastKnownUpdatedAt.value = data.updated_at ? new Date(data.updated_at).getTime() : Date.now()
      
      // OPTIMIZATION: Show UI immediately with basic test run info, then load details in background
      loading.value = false
      
      // OPTIMIZATION: Defer status update to after initial load (don't block page rendering)
      // If the test run has test cases but status is still "not_started" or "yet_to_start", recalculate it
      const shouldUpdateStatus = () => {
      const normalizedStatus = data.status?.toLowerCase()
      if (data.test_cases && data.test_cases.length > 0 && (normalizedStatus === 'not_started' || normalizedStatus === 'yet_to_start')) {
        const calculatedStatus = calculateTestRunStatus(data.test_cases)
        if (calculatedStatus !== data.status) {
          data.status = calculatedStatus
          testRun.value = data
            // Update in database asynchronously (don't block)
            testRunService.update(data.id, data).catch(error => {
            // Error updating status in database, continue
            })
          }
        }
      }
      
      // OPTIMIZATION: Load detailed data in background (non-blocking) - allows UI to render first
      const loadDetailedData = async () => {
        const parallelStartTime = performance.now()
        const [teamMembersResult,,, testCasesResult] = await Promise.all([
        // Load team members (can be used by creator name as fallback)
          loadTeamMembers(),
      // Load creator name (can use team members as fallback)
          loadCreatorName(),
      // Load linked feature if present
          loadLinkedFeatures(),
      // Load folders once (cached for folder tree building)
          loadFolders(),
      // Load test run cases after test run is loaded
          loadTestRunCases(),
      // Load activities after test run is loaded
          loadActivities()
        ])
      
        if (teamMembersResult?.apiCallCount) {
          apiCallCount += teamMembersResult.apiCallCount
        } else {
          apiCallCount += 1 // At least 1 call (team/project lookup)
        }
        // Increment API call count for test cases batch call
        if (testCasesResult?.apiCallCount) {
          apiCallCount += testCasesResult.apiCallCount
        } else if (testRun.value?.test_cases?.length > 0) {
          apiCallCount += 1 // Batch call was made
        }
        const parallelEndTime = performance.now()
        const parallelDuration = (parallelEndTime - parallelStartTime).toFixed(2)
        
        // OPTIMIZATION: Update status asynchronously after initial load (don't block UI)
        setTimeout(() => {
          shouldUpdateStatus()
        }, 0)
        
        // Performance tracking (run after detailed data loads)
        const totalEndTime = performance.now()
        const totalDuration = (totalEndTime - totalStartTime).toFixed(2)
        restoreFetch() // Stop tracking
        
        // Analyze all API calls
        const completedCalls = allApiCalls.filter(c => c.status !== 'pending')
        const optimizedCalls = apiCallCount
        const totalCalls = completedCalls.length
        
        // Group calls by type
        const callGroups = {}
        completedCalls.forEach(call => {
          const category = call.endpoint.includes('batch-fetch') ? 'BATCH (Optimized)'
            : call.endpoint.includes('test_cases') ? 'Test Cases'
            : call.endpoint.includes('users') ? 'Users'
            : call.endpoint.includes('projects') ? 'Projects'
            : call.endpoint.includes('teams') ? 'Teams'
            : call.endpoint.includes('folders') ? 'Folders'
            : call.endpoint.includes('bugs') ? 'Bugs'
            : call.endpoint.includes('features') ? 'Features'
            : call.endpoint.includes('test_runs') ? 'Test Runs'
            : call.endpoint.includes('health') ? 'Health'
            : call.endpoint.includes('me') ? 'Auth'
            : 'Other'
          
          if (!callGroups[category]) {
            callGroups[category] = []
          }
          callGroups[category].push(call)
        })
        
        // Calculate optimization impact
        const wouldHaveBeen = testRun.value?.test_cases?.length || 0
        const saved = wouldHaveBeen > 0 ? wouldHaveBeen - optimizedCalls : 0
        
        if (saved > 0) {
          const reductionPercent = wouldHaveBeen > 0 ? Math.round((saved / wouldHaveBeen) * 100) : 0
        }
      }
      
      // Start loading detailed data in background (non-blocking)
      loadDetailedData().catch(error => {
        console.error('Error loading detailed test run data:', error)
        restoreFetch() // Stop tracking on error
      })
    } catch (error) {
      // Try fallback with database service
      try {
        // Ensure database service is initialized
        if (!testRunService.db || !testRunService.db.initialized) {
          const { initializeDatabase } = await import('@/database')
          await initializeDatabase()
        }
        
        if (testRunService && testRunService.getById) {
          const response = await testRunService.getById(testRunId)
          testRun.value = response
          
          // OPTIMIZATION: Show UI immediately, then load details in background
          loading.value = false
          
          // Load all related data in background (non-blocking)
          const loadFallbackData = async () => {
          await loadTeamMembers()
            await Promise.all([
              loadCreatorName(),
              loadLinkedFeatures(),
              loadTestRunCases(),
              loadActivities()
            ])
          }
          
          loadFallbackData().catch(fallbackError => {
            console.error('Error loading fallback data:', fallbackError)
          })
        } else {
          showError('Error loading test run')
          loading.value = false
        }
      } catch (fallbackError) {
        showError('Error loading test run')
      loading.value = false
        restoreFetch() // Stop tracking on error
      }
    }
  }

  const loadCreatorName = async () => {
    try {
      if (testRun.value?.created_by) {
        // OPTIMIZATION: Wait a bit for team members to load (they're loading in parallel)
        // If team members are already loaded, use them immediately
        if (teamMembers.value.length > 0) {
        const member = teamMembers.value.find(m => m.id === testRun.value.created_by || m.uid === testRun.value.created_by)
        if (member) {
          createdByName.value = member.name || member.email || 'Unknown User'
          return
          }
        }
        
        // OPTIMIZATION: Try IndexedDB first (local, fast) before API call
        try {
          const { userService } = await import('@/database/services')
          const user = await userService.getById(testRun.value.created_by)
          if (user) {
            createdByName.value = user.name || user.email || 'Unknown User'
            return
          }
        } catch (dbError) {
          // IndexedDB not available or user not found, continue to batch fetch
        }
        
        // OPTIMIZATION: Check team members again (they might have loaded by now since we're in parallel)
        if (teamMembers.value.length > 0) {
          const member = teamMembers.value.find(m => m.id === testRun.value.created_by || m.uid === testRun.value.created_by)
          if (member) {
            createdByName.value = member.name || member.email || 'Unknown User'
            return
          }
        }
        
        // OPTIMIZATION: Use batch-fetch instead of individual API call
        // This ensures we use the same optimized endpoint as other user loads
        try {
          const { http } = await import('@/utils/httpClient')
          const users = await http.post('/users/batch-fetch', { userIds: [testRun.value.created_by] })
          if (users && users.length > 0) {
            const user = users[0]
            createdByName.value = user.name || user.email || 'Unknown User'
            return
          }
        } catch (batchError) {
          console.warn('Batch fetch for creator name failed:', batchError)
        }
        
        // If all methods fail, show Unknown User instead of ID
        createdByName.value = 'Unknown User'
      }
    } catch (error) {
      console.error('Error in loadCreatorName:', error)
      createdByName.value = 'Unknown User'
    }
  }

  // Load team members for assignee dropdown
  const loadTeamMembers = async () => {
    try {
      if (testRun.value?.project_id) {
        // OPTIMIZATION: Fetch project + team in ONE API call instead of two sequential calls
        const combinedFetchStart = performance.now()
        try {
          // Try to use optimized endpoint that returns project + team together
          const { http } = await import('@/utils/httpClient')
          const projectWithTeam = await http.get(`/projects/${testRun.value.project_id}?include=team`)
          
          const combinedFetchEnd = performance.now()
          const combinedDuration = (combinedFetchEnd - combinedFetchStart).toFixed(2)
          
          const project = projectWithTeam
          const team = projectWithTeam.team
          
          if (team && team.members && team.members.length > 0) {
            // OPTIMIZATION: Batch load all users in ONE API call instead of individual calls
            const batchStartTime = performance.now()
            try {
              // Use batch endpoint for faster loading (uses httpClient which handles auth automatically)
              const { http } = await import('@/utils/httpClient')
              const users = await http.post('/users/batch-fetch', { userIds: team.members })
              
              const batchEndTime = performance.now()
              const batchDuration = (batchEndTime - batchStartTime).toFixed(2)
              // Create a map for quick lookup
              const userMap = new Map(users.map(u => [u.id, u]))
              // Map team members to user objects, with fallback for missing users
              teamMembers.value = team.members.map(memberId => {
                const user = userMap.get(memberId)
                return user || { id: memberId, name: 'Unknown User', email: memberId }
              })
              
              // Also add team members to bugAssignees map for quick lookup
              teamMembers.value.forEach(member => {
                if (member.id) {
                  bugAssignees.value.set(member.id, member)
                }
                if (member.uid && member.uid !== member.id) {
                  bugAssignees.value.set(member.uid, member)
                }
              })
              
              return { apiCallCount: 2 } // Return API call count: 1 for project+team, 1 for batch users
            } catch (batchError) {
              const batchEndTime = performance.now()
              const batchDuration = (batchEndTime - batchStartTime).toFixed(2)
            }
          } else {
            return { apiCallCount: 1 } // Only project+team call made
          }
        } catch (optimizedError) {
          
          // FALLBACK: Use sequential calls if optimized endpoint doesn't exist yet
          const { projectService, teamService } = await import('@/database/services')
          
          // OPTIMIZATION: Fetch project first (needed to get teamId)
          const projectFetchStart = performance.now()
        const project = await projectService.getById(testRun.value.project_id)
          const projectFetchEnd = performance.now()
        
        if (project && project.teamId) {
            // OPTIMIZATION: Once we have teamId, fetch team and prepare for batch user load in parallel
            const teamFetchStart = performance.now()
          const team = await teamService.getById(project.teamId)
            const teamFetchEnd = performance.now()
          
          if (team && team.members && team.members.length > 0) {
              // OPTIMIZATION: Batch load all users in ONE API call instead of individual calls
              const batchStartTime = performance.now()
              try {
                // Use batch endpoint for faster loading (uses httpClient which handles auth automatically)
                const { http } = await import('@/utils/httpClient')
                const users = await http.post('/users/batch-fetch', { userIds: team.members })
                
                const batchEndTime = performance.now()
                const batchDuration = (batchEndTime - batchStartTime).toFixed(2)
                // Create a map for quick lookup
                const userMap = new Map(users.map(u => [u.id, u]))
                // Map team members to user objects, with fallback for missing users
                teamMembers.value = team.members.map(memberId => {
                  const user = userMap.get(memberId)
                  return user || { id: memberId, name: 'Unknown User', email: memberId }
                })
                
                // Also add team members to bugAssignees map for quick lookup
                teamMembers.value.forEach(member => {
                  if (member.id) {
                    bugAssignees.value.set(member.id, member)
                  }
                  if (member.uid && member.uid !== member.id) {
                    bugAssignees.value.set(member.uid, member)
                  }
                })
                
                return { apiCallCount: 3 } // Return API call count: 2 for project+team (sequential), 1 for batch users
              } catch (batchError) {
                const batchEndTime = performance.now()
                const batchDuration = (batchEndTime - batchStartTime).toFixed(2)
                console.warn('Batch user fetch failed, using placeholder users:', batchError)
                
                // OPTIMIZATION: Don't fall back to individual calls - just use placeholders
                // This prevents N individual API calls when batch fails
                teamMembers.value = team.members.map(memberId => ({
                  id: memberId,
                  name: 'Unknown User',
                  email: memberId
                }))
                return { apiCallCount: 2 } // Return call count: 2 for project+team (batch failed, no individual calls)
              }
          } else {
            teamMembers.value = []
              return { apiCallCount: 2 } // Only project+team calls made
          }
        } else {
          teamMembers.value = []
            return { apiCallCount: 2 } // Only project call made (no teamId)
        }
      }
      } else {
        teamMembers.value = []
        return { apiCallCount: 0 } // No project_id
      }
      return { apiCallCount: 0 } // No API calls made
    } catch (error) {
      console.error('❌ Error loading team members:', error)
      teamMembers.value = []
      return { apiCallCount: 0 }
    }
  }

  // Load linked feature (only if feature IDs exist)
  const loadLinkedFeatures = async () => {
    try {
      // Support both old feature_id and new feature_ids
      const featureIds = testRun.value?.feature_ids || (testRun.value?.feature_id ? [testRun.value.feature_id] : [])
      
      // Only fetch if there are feature IDs to load
      if (!featureIds || featureIds.length === 0) {
        linkedFeatures.value = []
        return
      }
      
      // OPTIMIZATION: Batch load all features in a single API call
          try {
            const { http } = await import('@/utils/httpClient')
        const features = await http.post('/features/batch-fetch', { featureIds })
        linkedFeatures.value = Array.isArray(features) ? features : []
      } catch (batchError) {
        // Fallback to individual calls if batch fails
        console.warn('Batch feature load failed, falling back to individual calls:', batchError)
            const { featureService } = await import('@/database/services')
            const features = await Promise.all(
              featureIds.map(id => featureService.getById(id).catch(err => {
                console.error(`Failed to load feature ${id}:`, err)
                return null
              }))
            )
            linkedFeatures.value = features.filter(f => f !== null)
      }
    } catch (error) {
      console.error('❌ Error loading linked features:', error)
      linkedFeatures.value = []
    }
  }

  // Navigate to feature detail page
  const viewFeature = (featureId) => {
    if (featureId && testRun.value?.project_id) {
      window.open(`/projects/${testRun.value.project_id}/features/${featureId}`, '_blank')
    }
  }

  const loadTestRunCases = async () => {
    try {
      loadingCases.value = true
      const testRunId = route.params.runId || route.params.id
      let testCaseApiCalls = 0
      
      // Since test cases are embedded in the test run document, we get them from there
      if (testRun.value && testRun.value.test_cases) {
        // Import folder service for batch loading
        const { folderService } = await import('@/database/services')
        
        // OPTIMIZATION: Batch load all test cases in ONE API call instead of individual calls
        const testCaseIds = testRun.value.test_cases.map(tc => tc.testCaseId).filter(Boolean)
        let testCasesMap = new Map()
        
        if (testCaseIds.length > 0) {
          const batchStartTime = performance.now()
            try {
            // OPTIMIZATION: For large lists, split into chunks to avoid URL length limits
            const CHUNK_SIZE = 100 // Process 100 test cases at a time
            const chunks = []
            for (let i = 0; i < testCaseIds.length; i += CHUNK_SIZE) {
              chunks.push(testCaseIds.slice(i, i + CHUNK_SIZE))
            }
            
            // Load all chunks in parallel
            const chunkPromises = chunks.map(async (chunk, chunkIndex) => {
              try {
                const testCaseIdsParam = chunk.length > 1 
                  ? chunk.join(',') 
                  : chunk[0]
                
                const chunkStartTime = performance.now()
                const batchResponse = await apiCall(`test_cases?testCaseId=${encodeURIComponent(testCaseIdsParam)}`)
                testCaseApiCalls++ // Track each chunk as one API call
                const chunkEndTime = performance.now()
                
                if (batchResponse.ok) {
                  const batchTestCases = await batchResponse.json()
                  // Handle both array and single object responses
                  const testCases = Array.isArray(batchTestCases) ? batchTestCases : [batchTestCases]
                  return testCases
                  } else {
                  try {
                    const errorText = await batchResponse.text()
                  } catch (e) {
                  }
                }
                return []
              } catch (error) {
                return []
              }
            })
            
            const allChunkResults = await Promise.all(chunkPromises)
            
            // Combine all results into map
            allChunkResults.flat().forEach(tc => {
              if (tc && tc.testCaseId) {
                testCasesMap.set(tc.testCaseId, tc)
              }
            })
            
            const batchEndTime = performance.now()
            const batchDuration = (batchEndTime - batchStartTime).toFixed(2)
            const loadedCount = testCasesMap.size
            const totalCalls = chunks.length
          } catch (batchError) {
            const batchEndTime = performance.now()
            const batchDuration = (batchEndTime - batchStartTime).toFixed(2)
          }
              }
              
        // OPTIMIZATION: Batch load ALL folders first, then build paths in memory (much faster!)
        const folderBatchStart = performance.now()
        const uniqueFolderIds = new Set()
        // Collect all unique folderIds from test cases
        testCasesMap.forEach(tc => {
          if (tc.folderId) uniqueFolderIds.add(tc.folderId)
        })
        // Also collect from test run cases that might have folderId
        testRun.value.test_cases.forEach(tc => {
          if (tc.folderId) uniqueFolderIds.add(tc.folderId)
        })
        
        const folderMap = new Map() // Map folderId -> folder object
        if (uniqueFolderIds.size > 0) {
          try {
            // OPTIMIZATION: Multi-pass loading strategy
            // Pass 1: Load all direct folders in parallel
            const folderIdsArray = Array.from(uniqueFolderIds)
            const folderPromises = folderIdsArray.map(async (folderId) => {
              try {
                    const folder = await folderService.getById(folderId)
                if (folder) {
                  folderMap.set(folderId, folder)
                  return folder
                  }
                } catch (error) {
                // Folder not found, skip
              }
              return null
            })
            
            const loadedFolders = (await Promise.all(folderPromises)).filter(f => f !== null)
            
            // Pass 2: Collect all parent folder IDs that we don't have yet
            const parentFolderIds = new Set()
            loadedFolders.forEach(folder => {
              if (folder?.parentId && !folderMap.has(folder.parentId)) {
                parentFolderIds.add(folder.parentId)
              }
            })
            
            // Pass 3: Load all parent folders in parallel (if any)
            if (parentFolderIds.size > 0) {
              const parentPromises = Array.from(parentFolderIds).map(async (parentId) => {
                try {
                  const parentFolder = await folderService.getById(parentId)
                  if (parentFolder) {
                    folderMap.set(parentId, parentFolder)
                    return parentFolder
                  }
                } catch (error) {
                  // Parent folder not found, skip
                }
                return null
              })
              
              const loadedParents = (await Promise.all(parentPromises)).filter(f => f !== null)
              
              // Pass 4: Recursively collect and load any remaining parent folders
              const remainingParents = new Set()
              loadedParents.forEach(folder => {
                if (folder?.parentId && !folderMap.has(folder.parentId)) {
                  remainingParents.add(folder.parentId)
                }
              })
              
              // Load remaining parents (usually just 1-2 levels deep)
              if (remainingParents.size > 0) {
                const remainingPromises = Array.from(remainingParents).map(async (parentId) => {
                  try {
                    const parentFolder = await folderService.getById(parentId)
                    if (parentFolder) {
                      folderMap.set(parentId, parentFolder)
                    }
                  } catch (error) {
                    // Skip if not found
                  }
                })
                await Promise.all(remainingPromises)
              }
            }
            
            const folderBatchEnd = performance.now()
            const folderBatchDuration = (folderBatchEnd - folderBatchStart).toFixed(2)
            const totalFoldersLoaded = folderMap.size
          } catch (error) {
          }
        } else {
        }
        
        // Build folder paths synchronously using pre-loaded folder data
        const buildFolderPathSync = (folderId) => {
          if (!folderId) return 'Root'
          const visited = new Set()
          const pathParts = []
          let currentId = folderId
          
          while (currentId && !visited.has(currentId)) {
            visited.add(currentId)
            const folder = folderMap.get(currentId)
            if (!folder) break
            pathParts.unshift(folder.name)
            currentId = folder.parentId
          }
          
          return pathParts.length > 0 ? pathParts.join('/') : 'Root'
        }
        
        // OPTIMIZATION: Batch load ALL unique users (executed_by, assigned_to) from test cases
        const userBatchStart = performance.now()
        const uniqueUserIds = new Set()
        testRun.value.test_cases.forEach(tc => {
          if (tc.executed_by) uniqueUserIds.add(tc.executed_by)
          if (tc.assigned_to) uniqueUserIds.add(tc.assigned_to)
        })
        // Also add users from team members (already loaded, but we'll merge them)
        const userMap = new Map(teamMembers.value.map(u => [u.id, u]))
        
        if (uniqueUserIds.size > 0) {
          try {
            // Filter out users we already have from team members
            const missingUserIds = Array.from(uniqueUserIds).filter(id => !userMap.has(id))
            
            if (missingUserIds.length > 0) {
              const { http } = await import('@/utils/httpClient')
              const users = await http.post('/users/batch-fetch', { userIds: missingUserIds })
              
              // Add to user map
              users.forEach(u => {
                if (u && u.id) {
                  userMap.set(u.id, u)
                }
              })
              
              const userBatchEnd = performance.now()
              const userBatchDuration = (userBatchEnd - userBatchStart).toFixed(2)
            } else {
            }
          } catch (userBatchError) {
          }
        } else {
        }
        
        // Transform test run cases using batch-loaded data (synchronous, fast!)
        
        const transformedCases = testRun.value.test_cases.map((tc, index) => {
          try {
            // Get test case from batch-loaded map (fast lookup, no API call)
            const actualTestCase = testCasesMap.get(tc.testCaseId) || null
            
            // DEBUG: Log if test case is missing from batch
            if (!actualTestCase && tc.testCaseId) {
            }
            
            // If test case not found in batch, use fallback data (don't make individual API calls)
            // This is acceptable - we have enough data from the test run document itself
              
            // Get folder path using pre-loaded folder data (synchronous, fast!)
            let folderPath = tc.folderPath
            if (!folderPath && actualTestCase?.folderId) {
                folderPath = buildFolderPathSync(actualTestCase.folderId)
            } else if (!folderPath) {
                folderPath = 'Root'
            }
            
            // Get user names from batch-loaded user map (no API calls)
            const executedByUser = tc.executed_by ? userMap.get(tc.executed_by) : null
            const assignedToUser = tc.assigned_to ? userMap.get(tc.assigned_to) : null
              
              const transformedCase = {
                id: `${testRun.value.id}_${tc.testCaseId}_${index}`, // Generate unique ID
                testCaseId: tc.testCaseId,
                testCaseDbId: actualTestCase?.id, // Store database id for navigation
                testCaseName: actualTestCase?.name || `Test Case ${tc.testCaseId}`,
                status: tc.status || 'not_started',
                executed_at: tc.executed_at,
                executed_by: tc.executed_by,
              executed_by_name: executedByUser?.name || executedByUser?.email || null, // Pre-loaded user name
                result: tc.result,
                notes: tc.notes || '',
                bugIds: tc.bugIds || [],
                assigneeId: tc.assigned_to || '',
              assignee_name: assignedToUser?.name || assignedToUser?.email || null, // Pre-loaded user name
                type: actualTestCase?.type || 'functional',
                caseType: actualTestCase?.case_type || 'sanity',
                automationStatus: actualTestCase?.automation_status || 'manual',
                priority: actualTestCase?.priority || 'medium',
                estimatedTime: actualTestCase?.estimated_time || '0m',
                folderPath: folderPath || 'Root',
                folderId: actualTestCase?.folderId || null
              }
              
              return transformedCase
            } catch (error) {
              // Fallback to basic info if test case not found
            const executedByUser = tc.executed_by ? userMap.get(tc.executed_by) : null
            const assignedToUser = tc.assigned_to ? userMap.get(tc.assigned_to) : null
            
              const fallbackCase = {
                id: `${testRun.value.id}_${tc.testCaseId}_${index}`,
                testCaseId: tc.testCaseId,
                testCaseDbId: null, // Database id not available in fallback
                testCaseName: `Test Case ${tc.testCaseId}`,
                status: tc.status || 'not_started',
                executed_at: tc.executed_at,
                executed_by: tc.executed_by,
              executed_by_name: executedByUser?.name || executedByUser?.email || null,
                result: tc.result,
                notes: tc.notes || '',
                bugIds: tc.bugIds || [],
                assigneeId: tc.assigned_to || '',
              assignee_name: assignedToUser?.name || assignedToUser?.email || null,
                type: 'functional',
                caseType: 'sanity',
                automationStatus: 'manual',
                priority: 'medium',
                estimatedTime: '0m',
                folderPath: tc.folderPath || 'Root',
                folderId: tc.folderId || null
              }
              return fallbackCase
            }
          })
        
        testRunCases.value = transformedCases
      } else {
        // Fallback to separate test run case documents if they exist
        const response = await testRunCaseStore.fetchTestRunCasesByTestRun(testRunId)
      testRunCases.value = response || []
      }
      
      // Return API call count for tracking
      return { apiCallCount: testCaseApiCalls }
    } catch (error) {
      showError('Error loading test run cases')
      return { apiCallCount: testCaseApiCalls }
    } finally {
      loadingCases.value = false
    }
  }

  const searchTestCases = async () => {
    if (!searchQuery.value.trim()) {
      availableTestCases.value = []
      return
    }

    try {
      searching.value = true
      const response = await testCaseStore.searchTestCases({
        query: searchQuery.value,
        projectId: testRun.value?.projectId
      })
      availableTestCases.value = response || []
    } catch (error) {
      showError('Error searching test cases')
    } finally {
      searching.value = false
    }
  }

  const isTestCaseAdded = (testCaseId) => {
    return testRunCases.value.some(trc => trc.testCaseId === testCaseId)
  }

  const addTestCaseToRun = async (testCase) => {
    try {
      const testRunId = route.params.runId || route.params.id
      await testRunCaseStore.createTestRunCase({
        test_run_id: testRunId,
        test_case_id: testCase.testCaseId || testCase.id, // Use testCaseId (display ID) if available
        project_id: route.params.projectId,
        status: 'not_started', // Use schema-compliant status
        created_by: authStore.user?.id || authStore.user?.uid,
        created_at: new Date(),
        updated_at: new Date()
      })
      
      // Update the test run data to include the new test case
      const updatedTestRun = { ...testRun.value }
      updatedTestRun.test_cases.push({
        testCaseId: testCase.testCaseId || testCase.id, // Use consistent testCaseId
        status: 'not_started',
        executed_at: null,
        executed_by: null,
        result: null,
        notes: '',
        assigned_to: null
      })
      
      // Update completion status based on progress
      updateCompletionStatus(updatedTestRun)
      
      // Update the test run in the database with a minimal payload to avoid overwriting concurrent changes
      const payload = buildTestRunUpdatePayload(updatedTestRun)
      await testRunService.update(testRunId, payload)
      testRun.value = updatedTestRun
      
      showToast('Test case added to test run')
      loadTestRunCases()
    } catch (error) {
      showError('Error adding test case to run')
    }
  }

  const executeTestCase = (testRunCase) => {
    // TODO: Implement test case execution
    showInfo('Test case execution coming soon')
  }


  const removeTestCase = async (testRunCase) => {
    if (!confirm(`Are you sure you want to remove "${testRunCase.testCaseName}" from this test run?`)) {
      return
    }

    try {
      const testRunId = route.params.runId || route.params.id
      
      // Update the test run data to remove the test case
      const updatedTestRun = { ...testRun.value }
      updatedTestRun.test_cases = updatedTestRun.test_cases.filter(tc => tc.testCaseId !== testRunCase.testCaseId)
      
      // Update completion status based on progress
      updateCompletionStatus(updatedTestRun)
      
      // Update with minimal payload to avoid overwriting concurrent changes
      const payload = buildTestRunUpdatePayload(updatedTestRun)
      await testRunService.update(testRunId, payload)
      testRun.value = updatedTestRun
      
      // Record activity
      addActivity('test_case_removed', `Removed test case "${testRunCase.testCaseName}" from test run`, {
        testCaseId: testRunCase.testCaseId,
        testCaseName: testRunCase.testCaseName
      })
      
      showToast('Test case removed from test run')
      loadTestRunCases()
    } catch (error) {
      showError('Error removing test case from run')
    }
  }

  const removeAllSelectedTestCases = async () => {
    if (!confirm(`Are you sure you want to remove ${selectedTestCases.size} selected test case${selectedTestCases.size > 1 ? 's' : ''} from this test run?`)) {
      return
    }

    try {
      const testRunId = route.params.runId || route.params.id
      const selectedCases = selectedTestCasesForRemoval.value
      
      // Update the test run data to remove all selected test cases
      const updatedTestRun = { ...testRun.value }
      const selectedIds = Array.from(selectedTestCases.value)
      updatedTestRun.test_cases = updatedTestRun.test_cases.filter(tc => !selectedIds.includes(tc.testCaseId))
      
      // Update completion status based on progress
      updateCompletionStatus(updatedTestRun)
      
      // Update with minimal payload to avoid overwriting concurrent changes
      const payload = buildTestRunUpdatePayload(updatedTestRun)
      await testRunService.update(testRunId, payload)
      testRun.value = updatedTestRun
      
      // Record activity
      const removedTestCaseNames = selectedCases.map(tc => tc.testCaseName).filter(name => name)
      addActivity('test_cases_removed', `Removed ${selectedCases.length} test case${selectedCases.length > 1 ? 's' : ''} from test run`, {
        count: selectedCases.length,
        testCaseIds: selectedIds,
        testCaseNames: removedTestCaseNames
      })
      
      // Clear selection
      selectedTestCases.value.clear()
      
      // Close modal
      showRemoveCasesModal.value = false
      
      showToast(`${selectedCases.length} test case${selectedCases.length > 1 ? 's' : ''} removed from test run`)
      loadTestRunCases()
    } catch (error) {
      showError('Error removing test cases from run')
    }
  }

  const startTestRun = async () => {
    try {
      starting.value = true
      const testRunId = route.params.runId || route.params.id
      await testRunService.update(testRunId, {
        status: TEST_RUN_STATUS_VALUES.IN_PROGRESS,
        started_at: new Date()
      })
      
      showToast('Test run started successfully')
      loadTestRun()
    } catch (error) {
      showError('Error starting test run')
    } finally {
      starting.value = false
    }
  }

  const pauseTestRun = async () => {
    if (!confirm(`Are you sure you want to pause this test run? This test run will be marked as Paused.`)) return
    
    try {
      const testRunId = route.params.runId || route.params.id
      await testRunService.update(testRunId, {
        status: 'PAUSED',
        updated_at: new Date()
      })
      
      // Add activity
      addActivity('test_run_paused', 'Test run has been paused', {
        testRunId: testRunId,
        testRunName: testRun.value?.name
      })
      
      loadTestRun()
    } catch (error) {
      showError('Error pausing test run')
    }
  }

  const resumeTestRun = async () => {
    if (!confirm(`Are you sure you want to resume this test run? This test run will be marked as In Progress.`)) return
    
    try {
      const testRunId = route.params.runId || route.params.id
      await testRunService.update(testRunId, {
        status: 'IN_PROGRESS',
        updated_at: new Date()
      })
      
      // Add activity
      addActivity('test_run_resumed', 'Test run has been resumed', {
        testRunId: testRunId,
        testRunName: testRun.value?.name
      })
      
      loadTestRun()
    } catch (error) {
      showError('Error resuming test run')
    }
  }

  const cancelTestRun = async () => {
    if (!confirm(`Are you sure you want to cancel this test run? This test run will be marked as Cancelled.`)) return
    
    try {
      const testRunId = route.params.runId || route.params.id
      await testRunService.update(testRunId, {
        status: 'CANCELLED',
        updated_at: new Date()
      })
      
      // Add activity
      addActivity('test_run_cancelled', 'Test run has been cancelled', {
        testRunId: testRunId,
        testRunName: testRun.value?.name
      })
      
      showToast('Test run cancelled successfully')
      loadTestRun()
    } catch (error) {
      showError('Error cancelling test run')
    }
  }

  const reopenTestRun = async () => {
    if (!confirm(`Are you sure you want to reopen this test run? This test run will be marked as Yet to Start.`)) return
    
    try {
      const testRunId = route.params.runId || route.params.id
      await testRunService.update(testRunId, {
        status: 'YET_TO_START',
        updated_at: new Date()
      })
      
      // Add activity
      addActivity('test_run_reopened', 'Test run has been reopened', {
        testRunId: testRunId,
        testRunName: testRun.value?.name
      })
      
      showToast('Test run reopened successfully')
      loadTestRun()
    } catch (error) {
      showError('Error reopening test run')
    }
  }

  const completeTestRun = async () => {
    try {
      completing.value = true
      const testRunId = route.params.runId || route.params.id
      await testRunService.update(testRunId, {
        status: 'completed',
        completed_at: new Date(),
        progress: 100
      })
      
      showToast('Test run completed successfully')
      loadTestRun()
    } catch (error) {
      showError('Error completing test run')
    } finally {
      completing.value = false
    }
  }

  const editTestRun = () => {
    // Navigate to edit test run page
    const projectId = route.params.projectId || route.params.id
    const testRunId = route.params.runId || route.params.id
    
    if (projectId && testRunId) {
      router.push(`/projects/${projectId}/test-runs/${testRunId}/edit?from=detail`)
    } else {
      // Fallback to general edit route if project context is not available
      router.push(`/test-runs/${testRunId}/edit?from=detail`)
    }
  }

  const cloneTestRun = async () => {
    if (!testRun.value || !authStore.user) {
      showError('Unable to clone test run: Missing test run or user information')
      return
    }

    const testRunId = route.params.runId || route.params.id
    if (!testRunId) {
      showError('Unable to clone test run: Test run ID not found')
      return
    }

    const clonerUserId = authStore.user.id || authStore.user.uid
    const clonerUserName = authStore.user.name || authStore.user.email || 'Unknown User'

    if (!clonerUserId) {
      showError('Unable to clone test run: User ID not found')
      return
    }

    try {
      cloning.value = true
      
      // Prepare cloned test run data (following the same logic as backend)
      const clonedData = {
        // Copy fields
        name: `${testRun.value.name || 'Test Run'} (Clone)`,
        description: testRun.value.description || '',
        prLinks: testRun.value.prLinks || '',
        executionNotes: testRun.value.executionNotes || '',
        feature_ids: testRun.value.feature_ids || [],
        estimated_time: testRun.value.estimated_time || 0,
        comments: [], // Clear comments - don't clone them
        
        // Reset fields
        environment: '',
        eta: '',
        urlToValidate: '',
        status: 'YET_TO_START',
        progress: 0,
        assigned_to: clonerUserId,
        
        // Preserve project
        project_id: testRun.value.project_id,
        
        // Clone test cases with status reset and bugs cleared (assignees are preserved)
        test_cases: (testRun.value.test_cases || []).map((tc) => ({
          ...tc,
          status: 'not_started',
          executed_at: null,
          executed_by: null,
          bugIds: [] // Clear bugs - don't clone them
          // assigned_to is preserved - will be cloned
        }))
      }
      
      // Store cloned data in sessionStorage
      const cloneStorageKey = `testRunClone_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem(cloneStorageKey, JSON.stringify(clonedData))
      
      // Navigate to edit page with cloneFrom query param
      const projectId = route.params.projectId || testRun.value?.project_id
      if (projectId) {
        router.push(`/projects/${projectId}/test-runs/clone/edit?cloneFrom=${testRunId}&cloneKey=${cloneStorageKey}`)
      } else {
        router.push(`/test-runs/clone/edit?cloneFrom=${testRunId}&cloneKey=${cloneStorageKey}`)
      }
    } catch (error) {
      console.error('Error cloning test run:', error)
      showError(error.message || 'Failed to clone test run. Please try again.')
    } finally {
      cloning.value = false
    }
  }

  const updateTestRun = async () => {
    try {
      editLoading.value = true
      
      const testRunId = route.params.runId || route.params.id
      const updatedData = {
        ...testRun.value,
        name: editForm.value.name,
        description: editForm.value.description,
        environment: editForm.value.environment,
        updated_at: new Date()
      }
      
      await testRunService.update(testRunId, updatedData)
      testRun.value = updatedData
      
      // Add activity
      const changes = {}
      
      // Record name change with before/after values
      if (editForm.value.name !== testRun.value.name) {
        changes.name = {
          before: testRun.value.name,
          after: editForm.value.name
        }
      }
      
      // Record description change with before/after values
      if (editForm.value.description !== testRun.value.description) {
        changes.description = {
          before: testRun.value.description,
          after: editForm.value.description
        }
      }
      
      // Record environment change with before/after values
      if (editForm.value.environment !== testRun.value.environment) {
        changes.environment = {
          before: testRun.value.environment,
          after: editForm.value.environment
        }
      }
      
      addActivity('test_run_updated', 'Updated test run details', {
        changes
      })
      
      showToast('Test run updated successfully')
      showEditModal.value = false
    } catch (error) {
      showError('Error updating test run')
    } finally {
      editLoading.value = false
    }
  }

  // Open test case in same tab (use router.push instead of window.open)
  const openTestCase = (testCaseIdOrDbId, event) => {
    const projectId = route.params.projectId || testRun.value?.project_id
    const runId = route.params.runId || route.params.id
    
    if (!projectId) {
      showError('Project context not found')
      return
    }
    
    // testCaseIdOrDbId could be either testCaseId or testCaseDbId
    // Try to find the test case in testRunCases to get the database id
    const testCase = testRunCases.value.find(tc => 
      tc.testCaseId === testCaseIdOrDbId || 
      tc.testCaseDbId === testCaseIdOrDbId ||
      tc.id === testCaseIdOrDbId
    )
    
    // Use database id if available, otherwise fallback to testCaseId
    const caseId = testCase?.testCaseDbId || testCaseIdOrDbId
    
    const url = `/projects/${projectId}/test-cases/${caseId}${runId ? `?testRunId=${runId}` : ''}`
    
    // Check for modifier keys (Command on Mac, Ctrl on Windows/Linux)
    if (event && (event.metaKey || event.ctrlKey)) {
      // Command/Ctrl + Click: Open in new tab but keep focus on current page
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
      // Try to keep focus on current window by blurring the new window
      if (newWindow) {
        setTimeout(() => {
          newWindow.blur()
          window.focus()
        }, 0)
      }
    } else {
      // Normal click: Open in new tab (will switch focus to new tab)
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  // Helper function to update completion status based on progress
  const updateCompletionStatus = (updatedTestRun) => {
    // Calculate progress based on completed test cases
    const totalCases = updatedTestRun.test_cases.length
    const completedCases = updatedTestRun.test_cases.filter(tc => 
      tc.status === 'passed' || 
      tc.status === 'failed' || 
      tc.status === 'retest' || 
      tc.status === 'skipped'
    ).length
    const newProgress = totalCases > 0 ? Math.round((completedCases / totalCases) * 100) : 0
    
    // Update progress
    updatedTestRun.progress = newProgress
    
    // Calculate status based on test case results
    const calculatedStatus = calculateTestRunStatus(updatedTestRun.test_cases)
    
    // Only update status if it's not manually set to PAUSED or CANCELLED
    const currentStatus = updatedTestRun.status?.toUpperCase()
    if (currentStatus !== 'PAUSED' && currentStatus !== 'CANCELLED') {
      updatedTestRun.status = calculatedStatus
    }
    
    // Handle completion timestamps
    if (calculatedStatus === 'COMPLETED' && updatedTestRun.status === 'COMPLETED') {
      if (!updatedTestRun.completed_at) {
        updatedTestRun.completed_at = new Date()
      }
    } else if (calculatedStatus !== 'COMPLETED') {
      // Clear completed_at when not completed
      updatedTestRun.completed_at = null
    }
    
    return updatedTestRun
  }

  // Build a minimal payload to avoid overwriting unrelated fields
  const buildTestRunUpdatePayload = (run) => ({
    test_cases: run.test_cases,
    progress: run.progress,
    status: run.status,
    started_at: run.started_at,
    completed_at: run.completed_at,
    updated_by: authStore.user?.id || null,
    updated_by_name: authStore.user?.name || authStore.user?.email || null
  })

  // Update test case status
  const updateTestCaseStatus = async (testCase) => {
    try {
      // Validate: If status is failed or blocked, bugIds must not be empty
      if ((testCase.status === 'failed' || testCase.status === 'blocked') && (!testCase.bugIds || testCase.bugIds.length === 0)) {
        showError(`Bug is required for ${testCase.status} status. Please select or create a bug first.`)
        // Revert status change in UI
        const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === testCase.testCaseId)
        if (testRunCaseIndex !== -1) {
          const originalTestCase = testRun.value.test_cases.find(tc => tc.testCaseId === testCase.testCaseId)
          if (originalTestCase) {
            testRunCases.value[testRunCaseIndex].status = originalTestCase.status
            testCase.status = originalTestCase.status
          }
        }
        return
      }
      
      const testRunId = route.params.runId || route.params.id
      
      // HYBRID APPROACH: Fetch latest data from DB before updating to prevent conflicts
      // This ensures we have the most recent data before making changes
      await refreshTestRunData()
      
      const currentTestCase = testRun.value.test_cases.find(tc => tc.testCaseId === testCase.testCaseId)
      if (!currentTestCase) {
        showError('Test case not found')
        return
      }
      
        const previousStatus = currentTestCase.status
        
      // Track pending change to prevent polling from overwriting
      pendingLocalChanges.value.set(testCase.testCaseId, Date.now())
      
      // Prepare minimal update payload - only send changed fields
      // Include expectedStatus to prevent race conditions (conflict detection)
      const updatePayload = {
        status: testCase.status,
        expectedStatus: previousStatus, // Send current status to detect conflicts
        updated_by: authStore.user?.id || null,
        updated_by_name: authStore.user?.name || authStore.user?.email || null
      }
        
        // Set executed_at and executed_by when status changes to any status other than "Not Started"
        if (testCase.status !== 'not_started') {
        updatePayload.executed_at = new Date()
        updatePayload.executed_by = authStore.user?.id || null
        }
        
      // Use optimized endpoint - backend will recalculate progress and status
      const response = await testRunService.updateTestCase(testRunId, testCase.testCaseId, updatePayload)
      
      // Clear pending change after successful update
      pendingLocalChanges.value.delete(testCase.testCaseId)
      
      // Update lastKnownUpdatedAt to prevent immediate re-polling
      if (response.updated_at) {
        lastKnownUpdatedAt.value = new Date(response.updated_at).getTime()
      }
      
      // Merge minimal response with local state (don't replace entire test run)
      if (response.updatedTestCase) {
        const testCaseIndex = testRun.value.test_cases.findIndex(tc => tc.testCaseId === testCase.testCaseId)
        if (testCaseIndex !== -1) {
          // Update the specific test case in the array
          testRun.value.test_cases[testCaseIndex] = {
            ...testRun.value.test_cases[testCaseIndex],
            ...response.updatedTestCase
          }
        }
      }
      
      // Update top-level fields
      if (response.progress !== undefined) testRun.value.progress = response.progress
      if (response.status !== undefined) testRun.value.status = response.status
      if (response.completed_at !== undefined) testRun.value.completed_at = response.completed_at
      if (response.updated_at !== undefined) testRun.value.updated_at = response.updated_at
      if (response.updated_by !== undefined) testRun.value.updated_by = response.updated_by
      if (response.updated_by_name !== undefined) testRun.value.updated_by_name = response.updated_by_name
        
        // Update testRunCases to reflect the changes in real-time
        const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === testCase.testCaseId)
      if (testRunCaseIndex !== -1 && response.updatedTestCase) {
        testRunCases.value[testRunCaseIndex] = {
          ...testRunCases.value[testRunCaseIndex],
          ...response.updatedTestCase
          }
        }

        // Add activity
        addActivity('status_update', `Updated test case status to ${testCase.status}`, {
          testCaseId: testCase.testCaseId,
          testCaseName: testCase.testCaseName,
          oldStatus: previousStatus,
          newStatus: testCase.status
        })
    } catch (error) {
      // Clear pending change on error
      pendingLocalChanges.value.delete(testCase.testCaseId)
      
      // Handle conflict errors specifically
      if (error?.message?.includes('Conflict:')) {
        showError(error.message)
        // Revert the UI change since the update failed
        const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === testCase.testCaseId)
        if (testRunCaseIndex !== -1) {
          const originalTestCase = testRun.value.test_cases.find(tc => tc.testCaseId === testCase.testCaseId)
          if (originalTestCase) {
            testRunCases.value[testRunCaseIndex].status = originalTestCase.status
            testCase.status = originalTestCase.status
          }
        }
        // Reload test run to get latest data
        await loadTestRun()
      } else {
      showError('Error updating test case status')
      }
    }
  }

  // Update test case assignee
  const updateTestCaseAssignee = async (testCase) => {
    try {
      const testRunId = route.params.runId || route.params.id
      
      // HYBRID APPROACH: Fetch latest data from DB before updating to prevent conflicts
      // This ensures we have the most recent data before making changes
      await refreshTestRunData()
      
      const currentTestCase = testRun.value.test_cases.find(tc => tc.testCaseId === testCase.testCaseId)
      if (!currentTestCase) {
        showError('Test case not found')
        return
      }
      
      const previousAssignee = currentTestCase.assigned_to
      
      // Track pending change to prevent polling from overwriting
      pendingLocalChanges.value.set(testCase.testCaseId, Date.now())
      
      // Prepare minimal update payload - only send changed fields
      // Add optimistic locking: send expected assignee to detect conflicts
      const updatePayload = {
        assigned_to: testCase.assigneeId,
        expectedAssignee: previousAssignee, // For conflict detection
        updated_by: authStore.user?.id || null,
        updated_by_name: authStore.user?.name || authStore.user?.email || null
      }
        
        // OPTIMISTIC UPDATE: Update testRunCases immediately to reflect changes in UI
        // This ensures the UI shows the change even if the database update is delayed
        const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === testCase.testCaseId)
        if (testRunCaseIndex !== -1) {
          testRunCases.value[testRunCaseIndex].assigned_to = testCase.assigneeId
          testRunCases.value[testRunCaseIndex].assigneeId = testCase.assigneeId // Also update assigneeId to keep in sync
        // CRITICAL: Update assignee_name so the dropdown shows the name instead of ID
        if (testCase.assigneeId) {
          const assignee = teamMembers.value.find(m => m.id === testCase.assigneeId)
          testRunCases.value[testRunCaseIndex].assignee_name = assignee ? (assignee.name || assignee.email || null) : null
        } else {
          testRunCases.value[testRunCaseIndex].assignee_name = null
        }
          testRunCases.value[testRunCaseIndex].updated_at = new Date()
        }
        
      // Use optimized endpoint - backend will recalculate progress and status
      const response = await testRunService.updateTestCase(testRunId, testCase.testCaseId, updatePayload)
      
      // Merge minimal response with local state (don't replace entire test run)
      if (response.updatedTestCase) {
        const testCaseIndex = testRun.value.test_cases.findIndex(tc => tc.testCaseId === testCase.testCaseId)
        if (testCaseIndex !== -1) {
          testRun.value.test_cases[testCaseIndex] = {
            ...testRun.value.test_cases[testCaseIndex],
            ...response.updatedTestCase
          }
        }
      }
      
      // Update top-level fields
      if (response.progress !== undefined) testRun.value.progress = response.progress
      if (response.status !== undefined) testRun.value.status = response.status
      if (response.completed_at !== undefined) testRun.value.completed_at = response.completed_at
      if (response.updated_at !== undefined) testRun.value.updated_at = response.updated_at
      if (response.updated_by !== undefined) testRun.value.updated_by = response.updated_by
      if (response.updated_by_name !== undefined) testRun.value.updated_by_name = response.updated_by_name
      
      // Update testRunCases
      const testRunCaseIndexForUpdate = testRunCases.value.findIndex(tc => tc.testCaseId === testCase.testCaseId)
      if (testRunCaseIndexForUpdate !== -1 && response.updatedTestCase) {
        testRunCases.value[testRunCaseIndexForUpdate] = {
          ...testRunCases.value[testRunCaseIndexForUpdate],
          ...response.updatedTestCase
        }
        // CRITICAL: Ensure assignee_name is set correctly after response
        if (response.updatedTestCase.assigned_to !== undefined) {
          if (response.updatedTestCase.assigned_to) {
            const assignee = teamMembers.value.find(m => m.id === response.updatedTestCase.assigned_to)
            testRunCases.value[testRunCaseIndexForUpdate].assignee_name = assignee ? (assignee.name || assignee.email || null) : null
          } else {
            testRunCases.value[testRunCaseIndexForUpdate].assignee_name = null
          }
        }
        }

        // Add activity
        const assigneeName = testCase.assigneeId ? 
          (teamMembers.value.find(m => m.id === testCase.assigneeId)?.name || 'Unknown') : 
          'Unassigned'
        const previousAssigneeName = previousAssignee ? 
          (teamMembers.value.find(m => m.id === previousAssignee)?.name || 'Unknown') : 
          'Unassigned'
        
        addActivity('assignee_update', `Updated test case assignee from ${previousAssigneeName} to ${assigneeName}`, {
          testCaseId: testCase.testCaseId,
          testCaseName: testCase.testCaseName,
          oldAssignee: previousAssignee,
          newAssignee: testCase.assigneeId,
          oldAssigneeName: previousAssigneeName,
          newAssigneeName: assigneeName
        })
    } catch (error) {
      showError('Error updating test case assignee')
    }
  }

  // Update test case bugs
  const updateTestCaseBugs = async (testCase) => {
    try {
      
      // Validate: If status is failed or blocked, bugIds must not be empty
      if ((testCase.status === 'failed' || testCase.status === 'blocked') && (!testCase.bugIds || testCase.bugIds.length === 0)) {
        showError(`Bug is required for ${testCase.status} status`)
        return
      }
      
      const testRunId = route.params.runId || route.params.id
      
      // HYBRID APPROACH: Fetch latest data from DB before updating to prevent conflicts
      // This ensures we have the most recent data before making changes
      await refreshTestRunData()
      
      const currentTestCase = testRun.value.test_cases.find(tc => tc.testCaseId === testCase.testCaseId)
      if (!currentTestCase) {
        showError('Test case not found')
        return
      }
      
      const previousBugIds = currentTestCase.bugIds || []
      
      // Prepare minimal update payload - only send changed fields
      const updatePayload = {
        bugIds: testCase.bugIds || [],
        updated_by: authStore.user?.id || null,
        updated_by_name: authStore.user?.name || authStore.user?.email || null
      }
      
      // Use optimized endpoint - backend will recalculate progress and status
      const response = await testRunService.updateTestCase(testRunId, testCase.testCaseId, updatePayload)
      
      // Merge minimal response with local state (don't replace entire test run)
      if (response.updatedTestCase) {
        const testCaseIndex = testRun.value.test_cases.findIndex(tc => tc.testCaseId === testCase.testCaseId)
      if (testCaseIndex !== -1) {
          testRun.value.test_cases[testCaseIndex] = {
            ...testRun.value.test_cases[testCaseIndex],
            ...response.updatedTestCase
          }
        }
      }
      
      // Update top-level fields
      if (response.progress !== undefined) testRun.value.progress = response.progress
      if (response.status !== undefined) testRun.value.status = response.status
      if (response.completed_at !== undefined) testRun.value.completed_at = response.completed_at
      if (response.updated_at !== undefined) testRun.value.updated_at = response.updated_at
      if (response.updated_by !== undefined) testRun.value.updated_by = response.updated_by
      if (response.updated_by_name !== undefined) testRun.value.updated_by_name = response.updated_by_name
        
        // Update testRunCases to reflect the changes in real-time
        const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === testCase.testCaseId)
      if (testRunCaseIndex !== -1 && response.updatedTestCase) {
        testRunCases.value[testRunCaseIndex] = {
          ...testRunCases.value[testRunCaseIndex],
          ...response.updatedTestCase
        }
        }
        
        // Link newly added bugs to test case
        const { testCaseService, bugService } = await import('@/database/services')
        
        // Find added bugs (only link new ones, don't unlink removed ones)
        const addedBugIds = (testCase.bugIds || []).filter(id => !previousBugIds.includes(id))
        const removedBugIds = previousBugIds.filter(id => !(testCase.bugIds || []).includes(id))
        
        // Link new bugs to test case
        for (const bugId of addedBugIds) {
          try {
            await testCaseService.linkBug(testCase.testCaseId, bugId, authStore.user?.id)
          } catch (error) {
            console.error('Error linking bug:', error)
          }
        }
        
        // NOTE: We do NOT unlink bugs from the test case when removing them from the test run
        // The bugs remain linked to the test case itself, just not visible in this specific test run
        if (removedBugIds.length > 0) {
        }
        
    } catch (error) {
      console.error('Error updating test case bugs:', error)
      showError('Error updating test case bugs')
    }
  }

  // Update test case notes
  const updateTestCaseNotes = async (testCase) => {
    try {
      const testRunId = route.params.runId || route.params.id
      
      // HYBRID APPROACH: Fetch latest data from DB before updating to prevent conflicts
      // This ensures we have the most recent data before making changes
      await refreshTestRunData()
      
      const currentTestCase = testRun.value.test_cases.find(tc => tc.testCaseId === testCase.testCaseId)
      if (!currentTestCase) {
        showError('Test case not found')
        return
      }
      
      // Prepare minimal update payload - only send changed fields
      const updatePayload = {
        notes: testCase.notes || '',
        updated_by: authStore.user?.id || null,
        updated_by_name: authStore.user?.name || authStore.user?.email || null
      }
      
      // Use optimized endpoint - backend will recalculate progress and status
      const response = await testRunService.updateTestCase(testRunId, testCase.testCaseId, updatePayload)
      
      // Merge minimal response with local state (don't replace entire test run)
      if (response.updatedTestCase) {
        const testCaseIndex = testRun.value.test_cases.findIndex(tc => tc.testCaseId === testCase.testCaseId)
      if (testCaseIndex !== -1) {
          testRun.value.test_cases[testCaseIndex] = {
            ...testRun.value.test_cases[testCaseIndex],
            ...response.updatedTestCase
          }
        }
      }
      
      // Update top-level fields
      if (response.progress !== undefined) testRun.value.progress = response.progress
      if (response.status !== undefined) testRun.value.status = response.status
      if (response.completed_at !== undefined) testRun.value.completed_at = response.completed_at
      if (response.updated_at !== undefined) testRun.value.updated_at = response.updated_at
      if (response.updated_by !== undefined) testRun.value.updated_by = response.updated_by
      if (response.updated_by_name !== undefined) testRun.value.updated_by_name = response.updated_by_name
        
        // Update testRunCases to reflect the changes in real-time
        const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === testCase.testCaseId)
      if (testRunCaseIndex !== -1 && response.updatedTestCase) {
        testRunCases.value[testRunCaseIndex] = {
          ...testRunCases.value[testRunCaseIndex],
          ...response.updatedTestCase
        }
        }
        
        showToast('Test case notes updated')
    } catch (error) {
      console.error('Error updating test case notes:', error)
      showError('Error updating test case notes')
    }
  }

  // Create bug for test case
  const createBugForTestCase = (testCase) => {
    // Navigate to bug creation page with test case pre-selected
    const projectId = route.params.projectId || testRun.value?.project_id
    if (projectId) {
      const url = `/projects/${projectId}/bugs/create?testCaseId=${testCase.testCaseId}&testCaseName=${encodeURIComponent(testCase.testCaseName || '')}&testRunId=${route.params.runId || route.params.id}`
      router.push(url)
    } else {
      showError('Project context not found')
    }
  }

  // Load bugs for the project
  const loadBugs = async () => {
    try {
      if (testRun.value?.project_id) {
        const { bugService } = await import('@/database/services')
        const bugs = await bugService.getByProject(testRun.value.project_id)
        
        // Filter to only show non-deleted bugs
        availableBugs.value = bugs.filter(bug => !bug.is_deleted)
      }
    } catch (error) {
      console.error('Error loading bugs:', error)
      availableBugs.value = []
    }
  }

  // View bug details in new tab (updated to open in new tab)
  const viewBug = (bugId) => {
    viewBugInNewTab(bugId)
  }

  // Selection functions
  const toggleTestCaseSelection = (testCaseId) => {
    if (selectedTestCases.value.has(testCaseId)) {
      selectedTestCases.value.delete(testCaseId)
    } else {
      selectedTestCases.value.add(testCaseId)
    }
  }

  const selectAllTestCases = () => {
    filteredTestCases.value.forEach(tc => {
      selectedTestCases.value.add(tc.testCaseId)
    })
  }

  const deselectAllTestCases = () => {
    selectedTestCases.value.clear()
  }

  const toggleFolderSelection = (folderId) => {
    // Find the folder in the tree and get all its test cases (including children)
    const getAllTestCasesInFolder = (folder) => {
      let testCases = [...folder.testCases]
      folder.children.forEach(child => {
        testCases = testCases.concat(getAllTestCasesInFolder(child))
      })
      return testCases
    }
    
    const findFolderById = (folders, targetId) => {
      for (const folder of folders) {
        if (folder.id === targetId) {
          return folder
        }
        const found = findFolderById(folder.children, targetId)
        if (found) return found
      }
      return null
    }
    
    const folder = findFolderById(testCasesByFolder.value, folderId)
    if (!folder) return
    
    const allTestCases = getAllTestCasesInFolder(folder)
    const allSelected = allTestCases.every(tc => selectedTestCases.value.has(tc.testCaseId))
    
    if (allSelected) {
      allTestCases.forEach(tc => selectedTestCases.value.delete(tc.testCaseId))
    } else {
      allTestCases.forEach(tc => selectedTestCases.value.add(tc.testCaseId))
    }
  }

  // Bulk operations
  const bulkUpdateTestCases = async () => {
    if ((!bulkUpdateStatus.value && bulkUpdateAssignee.value === '') || selectedTestCases.value.size === 0) {
      showError('Please select a status or assignee and at least one test case')
      return
    }

    try {
      bulkUpdateLoading.value = true
      const testRunId = route.params.runId || route.params.id
      
      // HYBRID APPROACH: Fetch latest data from DB before updating to prevent conflicts
      // This ensures we have the most recent data before making bulk changes
      await refreshTestRunData()
      
      // Track pending changes for all selected test cases
      const selectedTestCaseIds = Array.from(selectedTestCases.value)
      selectedTestCaseIds.forEach(testCaseId => {
        pendingLocalChanges.value.set(testCaseId, Date.now())
      })
      
      // Build minimal update payload - only changed fields for each test case
      const updates = selectedTestCaseIds.map(testCaseId => {
        const currentTestCase = testRun.value.test_cases.find(tc => tc.testCaseId === testCaseId)
        const updateData = {
          updated_by: authStore.user?.id || null,
          updated_by_name: authStore.user?.name || authStore.user?.email || null
        }
        
        // Add status update if provided
          if (bulkUpdateStatus.value) {
          updateData.status = bulkUpdateStatus.value
          // Include expectedStatus for conflict detection
          if (currentTestCase) {
            updateData.expectedStatus = currentTestCase.status
          }
            // Set executed_at and executed_by when status changes to any status other than "Not Started"
            if (bulkUpdateStatus.value !== 'not_started') {
            updateData.executed_at = new Date()
            updateData.executed_by = authStore.user?.id || null
            }
          }
          
        // Add assignee update if provided
          if (bulkUpdateAssignee.value !== '') {
          updateData.assigned_to = bulkUpdateAssignee.value === 'unassigned' ? '' : bulkUpdateAssignee.value
          // Include expectedAssignee for conflict detection
          if (currentTestCase) {
            updateData.expectedAssignee = currentTestCase.assigned_to || null
          }
        }
        
        return {
          testCaseId,
          updateData
        }
      })

      // Use optimized bulk update endpoint - backend will recalculate progress and status
      const response = await testRunService.bulkUpdateTestCases(testRunId, updates)
      
      // Clear pending changes after successful update
      selectedTestCaseIds.forEach(testCaseId => {
        pendingLocalChanges.value.delete(testCaseId)
      })
      
      // Update lastKnownUpdatedAt to prevent immediate re-polling
      if (response.updated_at) {
        lastKnownUpdatedAt.value = new Date(response.updated_at).getTime()
      }
      
      // Merge minimal response with local state (don't replace entire test run)
      if (response.updatedTestCases && response.updatedTestCases.length > 0) {
        response.updatedTestCases.forEach(updatedTestCase => {
          const testCaseIndex = testRun.value.test_cases.findIndex(tc => tc.testCaseId === updatedTestCase.testCaseId)
          if (testCaseIndex !== -1) {
            testRun.value.test_cases[testCaseIndex] = {
              ...testRun.value.test_cases[testCaseIndex],
              ...updatedTestCase
            }
          }
        })
        // Trigger reactivity by reassigning the array
        testRun.value.test_cases = [...testRun.value.test_cases]
      }
      
      // Update top-level fields
      if (response.progress !== undefined) testRun.value.progress = response.progress
      if (response.status !== undefined) testRun.value.status = response.status
      if (response.completed_at !== undefined) testRun.value.completed_at = response.completed_at
      if (response.updated_at !== undefined) testRun.value.updated_at = response.updated_at
      if (response.updated_by !== undefined) testRun.value.updated_by = response.updated_by
      if (response.updated_by_name !== undefined) testRun.value.updated_by_name = response.updated_by_name
      
      // Update testRunCases to reflect the changes in real-time
      if (response.updatedTestCases && response.updatedTestCases.length > 0) {
        response.updatedTestCases.forEach(updatedTestCase => {
          const testRunCaseIndex = testRunCases.value.findIndex(tc => tc.testCaseId === updatedTestCase.testCaseId)
          if (testRunCaseIndex !== -1) {
            testRunCases.value[testRunCaseIndex] = {
              ...testRunCases.value[testRunCaseIndex],
              ...updatedTestCase
            }
            // Also update assigneeId and assignee_name for UI consistency
            if (updatedTestCase.assigned_to !== undefined) {
              testRunCases.value[testRunCaseIndex].assigneeId = updatedTestCase.assigned_to
              if (updatedTestCase.assigned_to) {
                const assignee = teamMembers.value.find(m => m.id === updatedTestCase.assigned_to)
              if (assignee) {
                testRunCases.value[testRunCaseIndex].assignee_name = assignee.name || assignee.email || null
              }
            } else {
              testRunCases.value[testRunCaseIndex].assignee_name = null
            }
          }
        }
      })
        // Trigger reactivity by reassigning the array
        testRunCases.value = [...testRunCases.value]
      }

      const updatedCount = selectedTestCases.value.size
      
      // Create activity description based on what was updated
      let activityDescription = `Bulk updated ${updatedCount} test case${updatedCount > 1 ? 's' : ''}`
      const updateDescriptions = []
      if (bulkUpdateStatus.value) {
        updateDescriptions.push(`status to ${bulkUpdateStatus.value}`)
      }
      if (bulkUpdateAssignee.value !== '') {
        const assigneeName = bulkUpdateAssignee.value === 'unassigned' ? 
          'Unassigned' : 
          (teamMembers.value.find(m => m.id === bulkUpdateAssignee.value)?.name || 'Unknown')
        updateDescriptions.push(`assignee to ${assigneeName}`)
      }
      activityDescription += ` (${updateDescriptions.join(', ')})`
      
      // Get test case names for activity details
      const updatedTestCaseNames = []
      selectedTestCases.value.forEach(testCaseId => {
        const testCase = testRunCases.value.find(tc => tc.testCaseId === testCaseId)
        if (testCase && testCase.testCaseName) {
          updatedTestCaseNames.push(testCase.testCaseName)
        }
      })

      // Add activity
      addActivity('bulk_update', activityDescription, {
        count: updatedCount,
        status: bulkUpdateStatus.value,
        assignee: bulkUpdateAssignee.value,
        testCaseNames: updatedTestCaseNames
      })

      // Clear selection and reset form
      selectedTestCases.value.clear()
      bulkUpdateStatus.value = ''
      bulkUpdateAssignee.value = ''
    } catch (error) {
      // Clear pending changes on error
      const selectedTestCaseIds = Array.from(selectedTestCases.value)
      selectedTestCaseIds.forEach(testCaseId => {
        pendingLocalChanges.value.delete(testCaseId)
      })
      
      // Check if it's a conflict error
      if (error.message && error.message.includes('Conflict')) {
        showError(error.message)
        // Reload test run to get latest data
        await loadTestRun()
      } else {
      showError('Error updating test cases')
      }
    } finally {
      bulkUpdateLoading.value = false
    }
  }

  const clearFilters = () => {
    filters.value = {
      status: [],
      type: [],
      caseType: [],
      automationStatus: [],
      priority: [],
      assignee: [],
      search: ''
    }
  }

  // Field editor functions
  const toggleField = (fieldKey, isMandatory, checked) => {
    // Don't allow toggling mandatory fields
    if (isMandatory) return
    
    const index = visibleFields.value.indexOf(fieldKey)
    if (checked && index === -1) {
      visibleFields.value.push(fieldKey)
    } else if (!checked && index > -1) {
      visibleFields.value.splice(index, 1)
    }
  }

  const resetFields = () => {
    // Only select mandatory fields (select column is always included)
    const mandatoryFields = availableFields.value.filter(field => field.mandatory).map(field => field.key)
    visibleFields.value = ['select', ...mandatoryFields]
    
    // Save reset state to localStorage
    saveFieldSelections(visibleFields.value)
  }

  const applyFieldChanges = () => {
    // Ensure mandatory fields are always included (select column is always included)
    const mandatoryFields = availableFields.value.filter(field => field.mandatory).map(field => field.key)
    
    // Always include mandatory fields
    mandatoryFields.forEach(field => {
      if (!visibleFields.value.includes(field)) {
        visibleFields.value.push(field)
      }
    })
    
    // Save field selections to localStorage
    saveFieldSelections(visibleFields.value)
    
    showFieldEditor.value = false
    showToast('Table fields updated successfully')
  }

  // Watch for route changes and scroll to top
  watch(() => route.params.runId || route.params.id, (newRunId, oldRunId) => {
    if (newRunId && newRunId !== oldRunId) {
      // Scroll to top when navigating to a different test run
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
  })

  // Watch for test run data changes and scroll to top
  watch(testRun, async (newTestRun) => {
    if (newTestRun) {
      // Scroll to top when test run data loads
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
      // Reload comments and bugs when test run data changes
      // Bugs are loaded upfront for better performance
      await Promise.all([
        loadComments(),
        loadLinkedBugs()
      ])
    }
  }, { immediate: true })

  // Watch for route changes to reload comments
  watch(() => route.params.runId || route.params.id, async (newRunId, oldRunId) => {
    if (newRunId && newRunId !== oldRunId) {
      await loadComments()
    }
  })

  // Watch for active tab changes to reload data when switching tabs
  watch(activeRightTab, async (newTab) => {
    if (newTab === 'comments') {
      await loadComments()
    }
    // Bugs are already loaded when test run is loaded, no need to reload on tab switch
  })

  // Store event handlers for cleanup
  let handleResize = null
  let handleBugCreated = null
  let isPageActive = true

  const cleanupRealtimeAndListeners = async () => {
    // If already cleaning up, skip
    if (!isPageActive) return
    isPageActive = false

    // Remove event listeners
    if (handleResize) {
      window.removeEventListener('resize', handleResize)
      handleResize = null
    }
    if (handleBugCreated) {
      window.removeEventListener('message', handleBugCreated)
      handleBugCreated = null
    }

    // Stop subscription + disconnect socket
    await stopWebSocketSubscription()
  }

  // Ensure cleanup runs even if async onMounted continues after navigation
  onBeforeRouteLeave(async () => {
    await cleanupRealtimeAndListeners()
  })

  // Defensive: if this view ever becomes keep-alive, cleanup on deactivate/restore on activate
  onDeactivated(async () => {
    await cleanupRealtimeAndListeners()
  })

  onActivated(() => {
    isPageActive = true
    // Re-subscribe if we have a loaded test run (keep-alive scenario)
    if (testRun.value?.id) {
      startWebSocketSubscription()
    }
  })

  // Check project access when test run is loaded
  const checkProjectAccess = async () => {
    if (testRun.value?.project_id) {
      try {
        hasProjectAccess.value = await canCUDTestRuns(testRun.value.project_id)
      } catch (error) {
        console.warn('Failed to check test run access:', error)
        hasProjectAccess.value = false
      }
    } else {
      hasProjectAccess.value = false
    }
  }

  // Watch testRun to check access when it changes
  watch(() => testRun.value, async () => {
    await checkProjectAccess()
  }, { immediate: true })

  onMounted(async () => {
    isPageActive = true
    // Scroll to top when component mounts
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 100)
    
    // Check access when component mounts
    await checkProjectAccess()
    
    // Track window width for responsive layout
    handleResize = () => {
      windowWidth.value = window.innerWidth
    }
    window.addEventListener('resize', handleResize)
    
    // Listen for bug creation messages from child windows
    handleBugCreated = async (event) => {
      // Verify message origin for security (adjust as needed)
      if (event.data && event.data.type === 'bugCreated') {
        const { bugId, testCaseId, testRunId } = event.data
        const currentTestRunId = route.params.runId || route.params.id
        
        // Only refresh if the bug was created for this test run
        if (testRunId === currentTestRunId) {
          // Reload test run to get updated bug IDs
          await loadTestRun()
          await loadBugs()
          
          // Show success message
          showToast('Bug created and added to test case')
        }
      }
    }
    window.addEventListener('message', handleBugCreated)
    
    await loadTestRun()
    if (!isPageActive) return
    // Load comments, bugs, and linked bugs after test run is loaded
    // (activities already loaded in loadTestRun, so skip duplicate call)
    await Promise.all([
      loadComments(),
      loadBugs(),
      loadLinkedBugs()
    ])
    if (!isPageActive) return
    
    // Start WebSocket subscription for real-time updates
    startWebSocketSubscription()
  })
  
  onUnmounted(async () => {
    await cleanupRealtimeAndListeners()
  })
  
  // Watch for test run ID changes to re-subscribe
  watch(() => route.params.runId || route.params.id, async (newTestRunId, oldTestRunId) => {
    if (newTestRunId && newTestRunId !== oldTestRunId) {
      // Unsubscribe from old test run (but don't disconnect - will reconnect for new test run)
      if (websocketUnsubscribe.value) {
        websocketUnsubscribe.value()
        websocketUnsubscribe.value = null
      }
      // Subscribe to new test run (reuses connection)
      await startWebSocketSubscription()
    }
  })

  const formatFullDateTime = (date) => {
    if (!date) return ''
    const d = new Date(date)
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }
    return d.toLocaleString('en-US', options)
  }

  const getActivityUserName = (activity) => {
    // If activity has user_name, use it (resolved in loadActivities)
    if (activity.user_name) return activity.user_name
    
    // If activity has userName property, use it
    if (activity.userName) return activity.userName
    
    // If activity has user_id, try to find the user
    if (activity.user_id) {
      const user = teamMembers.value.find(u => u.id === activity.user_id)
      if (user) return user.name || user.email
    }
    
    // If activity has created_by, try to find the user
    if (activity.created_by) {
      const user = teamMembers.value.find(u => u.id === activity.created_by)
      if (user) return user.name || user.email
    }
    
    // Check if current user is logged in and use their name
    if (authStore.user) {
      return authStore.user.name || authStore.user.email
    }
    
    // Fallback to "System"
    return 'System'
  }

  // Format activity description, resolving user IDs to names for assignee changes
  const formatActivityDescription = (activity) => {
    if (!activity.description) return activity.description || ''
    
    // Check if description contains assignee changes with user IDs
    // Pattern: "Updated test case assignee from user_xxx to user_yyy" or "assignee to user_xxx"
    const assigneePatterns = [
      /(?:Updated test case assignee|assignee)\s+from\s+([^\s]+)\s+to\s+([^\s]+)/i,
      /assignee\s+to\s+([^\s,)]+)/i
    ]
    
    for (const pattern of assigneePatterns) {
      const match = activity.description.match(pattern)
      if (match) {
        // Helper to get user name from ID
        const getUserName = (userId) => {
          if (!userId || userId === '' || userId === 'Unassigned' || userId === 'None') return 'Unassigned'
          // Check if it's already a name (doesn't start with 'user_' or look like an ID)
          if (!userId.startsWith('user_') && !userId.match(/^[a-z0-9_]+$/i)) {
            return userId // Already a name
          }
          const user = teamMembers.value.find(u => u.id === userId || u.uid === userId)
          return user ? (user.name || user.email || 'Unknown User') : userId
        }
        
        if (match[1] && match[2]) {
          // Has both old and new assignee
          const oldName = getUserName(match[1])
          const newName = getUserName(match[2])
          return activity.description.replace(pattern, `Updated test case assignee from ${oldName} to ${newName}`)
        } else if (match[1]) {
          // Has only new assignee
          const newName = getUserName(match[1])
          return activity.description.replace(pattern, (match) => {
            return match.replace(match[1], newName)
          })
        }
      }
    }
    
    // Return original description for other cases
    return activity.description
  }

  const getActivityIconClass = (type) => {
    const classes = {
      'created': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
      'status_changed': 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
      'status_update': 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
      'test_cases_added': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
      'test_case_added': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
      'test_cases_removed': 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400',
      'test_case_removed': 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400',
      'test_run_updated': 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400',
      'assignee_updated': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400',
      'bulk_update': 'bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400',
      'test_run_paused': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400',
      'test_run_resumed': 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400',
      'test_run_cloned': 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
      'comment_added': 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400',
      'default': 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    }
    return classes[type] || classes.default
  }
  </script>